import React, { useState, useEffect, useRef } from 'react';
import { formatSavedMemberAddress } from '../utils/clientChartAddress';
import { visitReadinessLabel } from '../utils/visitReadiness';
import { fetchClientDetailIfSelected } from '../utils/clientDetailRequest';
import { Client, ClientAddress, ClientPhone, UserRole, Authorization, ClientGoal, ServiceSession, ClinicalDocument, ClientNote } from '../types';
import { BridgeAuthorizationPanel } from './BridgeAuthorizationPanel';
import { canEditManualSoapApproval, manualSoapApprovalDisplay, ManualSoapApprovalModal } from './ManualSoapApprovalModal';
import { canEditCanonicalGoals } from '../utils/canonicalGoalDirect';
import { canManageClientStatus } from '../utils/clientStatusAccess';
import { canManageClientOperations } from '../utils/clientOperationsAccess';
import { displayAvailableUnits, isImportedAuthorization, isOpenManualBridge, isReplacedAuthorization } from '../utils/bridgeAuthorizationRules';
import {
  authorizationServiceDescription,
  chartAuthorizationTypeClass,
  chartAuthorizationTypeLabel,
  currentAuthorizationFlags,
  feedingAuthorizationStatusText,
  speechAuthorizationStatusText
} from '../utils/chartAuthorizationLabels';
import {
  Users,
  Plus,
  ArrowRight,
  ArrowLeft,
  Shield,
  ShieldCheck,
  FileText,
  Anchor,
  Target,
  Calendar,
  CircleDollarSign,
  AlertCircle,
  CheckCircle2,
  X,
  Phone,
  Mail,
  MapPin,
  Printer,
  Edit3,
  Stethoscope,
  Clock,
  Building,
  Send,
  Download,
  Eye,
  Upload,
  Paperclip,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  UserCheck,
  UserX,
  MessageSquare,
  Tag,
  Wrench,
  MoreHorizontal
} from 'lucide-react';
import { UniversalCallModal } from './UniversalCallModal';
import { ClientFaxModal } from './ClientFaxModal';
import { PocAuthExpirationsView } from './PocAuthExpirationsView';
import { ClientEditModal, ChartEditFocusSection } from './ClientEditModal';
import { FullIntakeModal } from './FullIntakeModal';
import { IconBadge } from './IconBadge';
import { confirmedFeedingThenSpeech, hasUnresolvedPocDateConflict, resolveClientPocStreams, splitPocDateParts, toPocDateInputValue, type PocStream } from '../utils/pocConflict';
import {
  CLINICAL_UPLOAD_DOC_TYPES,
  clinicianSigDisplay,
  defaultClinicalUploadTitle,
  derivePocActiveExpired,
  formatClinicalDocTypeLabel,
  guardianSigDisplay,
  isFeedingClinicalDoc,
  isPhysicianSignedCpoc,
  isSpeechOrFeedingPoc
} from '../utils/clinicalDocDisplay';

interface ClientsViewProps {
  userRole: UserRole;
  providerCredential?: string | null;
  selectedClientId: string | null;
  onSelectClient: (id: string | null) => void;
  onOpenAIWithContext: (prompt: string, clientId: string) => void;
  onOpenAIWithPrompt?: (prompt: string) => void;
  onOpenUniversalTools?: () => void;
  openIntakeTrigger?: number;
  openNewClientTrigger?: number;
  navTrigger?: {
    viewMode: 'roster' | 'expirations';
    filter?: 'all' | '30days' | '60days' | 'exhausted' | 'poc_only' | 'auth_only';
    timestamp: number;
  } | null;
  initialChartTab?: 'Overview' | 'Auths' | 'Goals' | 'Visits' | 'Clinical Docs' | 'Billing' | 'Notes';
}

function pocStreamBadgeClass(status?: string) {
  if (status === 'EXPIRED') return 'bg-rose-50 text-rose-700 border border-rose-200';
  if (status === 'EXPIRING') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (status === 'INACTIVE') return 'bg-slate-100 text-slate-600 border border-slate-200';
  if (status === 'PENDING_PHYSICIAN' || status === 'PENDING_RECERT' || status === 'CONFLICT_NEEDS_REVIEW') {
    return 'bg-slate-100 text-slate-700 border border-slate-200';
  }
  return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
}

function ChartPocStreamCard({ title, stream, onEdit }: { title: string; stream: PocStream; onEdit?: () => void }) {
  const status = stream.poc_status || 'ACTIVE';
  const endTime = stream.poc_end_date ? new Date(stream.poc_end_date).getTime() : NaN;
  const diffDays = status !== 'INACTIVE' && stream.poc_end_date && !isNaN(endTime)
    ? Math.ceil((endTime - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  return (
    <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 space-y-3 shadow-2xs">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase font-bold text-slate-500">{title}</span>
        <div className="flex items-center gap-2 shrink-0">
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="h-6 px-2 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 text-[10px] font-medium inline-flex items-center space-x-1"
              title={`Edit ${title}`}
            >
              <Edit3 className="w-3 h-3 text-slate-500" />
              <span>Edit</span>
            </button>
          )}
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-bold uppercase ${pocStreamBadgeClass(status)}`}>{status}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-2.5 border-t border-slate-200/80">
        <div>
          <span className="text-[10px] text-slate-500 block mb-1">POC Start Date:</span>
          <span className="font-mono text-slate-900 text-[11px] font-semibold">
            {stream.poc_start_date || <span className="text-slate-400 font-normal italic">Not set</span>}
          </span>
        </div>
        <div>
          <span className="text-[10px] text-slate-500 block mb-1">POC Expiration / End Date:</span>
          <span className="font-mono text-slate-900 text-[11px] font-semibold">
            {stream.poc_end_date || <span className="text-slate-400 font-normal italic">Not set</span>}
          </span>
        </div>
      </div>
      {diffDays != null && (
        <div className="pt-2.5 flex items-center justify-between border-t border-slate-200/80">
          <span className="text-[10px] text-slate-500">Timeline / Recert:</span>
          <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${
            diffDays <= 30
              ? 'bg-rose-50 text-rose-700 border border-rose-200'
              : diffDays <= 60
              ? 'bg-amber-50 text-amber-800 border border-amber-200'
              : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
          }`}>
            {diffDays <= 0 ? 'POC Expired' : `${diffDays}d remaining`}
          </span>
        </div>
      )}
    </div>
  );
}

export const ClientsView: React.FC<ClientsViewProps> = ({
  userRole,
  providerCredential,
  selectedClientId,
  onSelectClient,
  onOpenAIWithContext,
  onOpenAIWithPrompt,
  onOpenUniversalTools,
  openIntakeTrigger,
  openNewClientTrigger,
  navTrigger,
  initialChartTab
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'PENDING_INTAKE' | 'INACTIVE' | 'ALL'>('ACTIVE');
  const [editModalSection, setEditModalSection] = useState<ChartEditFocusSection>('general');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'roster' | 'expirations'>('roster');
  const [lastSelectedMode, setLastSelectedMode] = useState<'roster' | 'expirations'>('roster');
  const [expirationsFilter, setExpirationsFilter] = useState<'all' | '30days' | '60days' | 'exhausted' | 'poc_only' | 'auth_only'>('60days');
  const [filterTrigger, setFilterTrigger] = useState<number>(0);
  const selectedClientIdRef = useRef<string | null>(selectedClientId);
  selectedClientIdRef.current = selectedClientId;

  // Mobile/PWA popstate back navigation support: returns to client list without reloading
  useEffect(() => {
    if (selectedClientId) {
      window.history.pushState({ clientChartOpen: true }, '');
      const handlePopState = () => {
        onSelectClient(null);
        if (lastSelectedMode === 'expirations') {
          setViewMode('expirations');
        }
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [selectedClientId, onSelectClient, lastSelectedMode]);

  useEffect(() => {
    if (navTrigger) {
      setViewMode(navTrigger.viewMode);
      if (navTrigger.filter) {
        setExpirationsFilter(navTrigger.filter);
        setFilterTrigger(navTrigger.timestamp);
      }
    }
  }, [navTrigger]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInactiveConfirmModal, setShowInactiveConfirmModal] = useState(false);
  const [chartTab, setChartTab] = useState<'Overview' | 'Auths' | 'Goals' | 'Visits' | 'Clinical Docs' | 'Billing' | 'Notes'>('Overview');
  const isSecondaryChartTab = (tab: string) => ['Visits', 'Clinical Docs', 'Billing', 'Notes'].includes(tab);
  const [showSecondaryMobileNav, setShowSecondaryMobileNav] = useState(false);

  useEffect(() => {
    if (initialChartTab) {
      const isClinical = userRole === 'ASSISTANT' || userRole === 'SUPERVISOR';
      if (initialChartTab === 'Billing' && isClinical) {
        setChartTab('Overview');
      } else {
        setChartTab(initialChartTab);
      }
    }
  }, [initialChartTab, userRole]);

  useEffect(() => {
    if (isSecondaryChartTab(chartTab)) {
      setShowSecondaryMobileNav(true);
    } else {
      setShowSecondaryMobileNav(false);
    }
  }, [chartTab]);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showFaxModal, setShowFaxModal] = useState(false);
  const [allProviders, setAllProviders] = useState<any[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [chartRemoteSignatureAssignment, setChartRemoteSignatureAssignment] = useState<any>(null);
  const [assignForm, setAssignForm] = useState({
    provider_id: '',
    supervisor_id: '',
    expected_frequency: '2x/week',
    expected_duration_minutes: 60,
    notes: ''
  });
  const [pocResolveOpen, setPocResolveOpen] = useState(false);
  const [pocResolveStart, setPocResolveStart] = useState('');
  const [pocResolveEnd, setPocResolveEnd] = useState('');
  const [pocResolveStream, setPocResolveStream] = useState('');
  const [pocResolveSaving, setPocResolveSaving] = useState(false);
  const [pocResolveError, setPocResolveError] = useState('');
  const [bridgePanel, setBridgePanel] = useState<null | { mode: 'replace' | 'correct'; authId: string }>(null);
  const [bridgeRefreshNonce, setBridgeRefreshNonce] = useState(0);
  const [clientDetail, setClientDetail] = useState<{
    client: Client;
    guardians: any[];
    authorizations: Authorization[];
    goals: ClientGoal[];
    services: ServiceSession[];
    documents: ClinicalDocument[];
    billing: any[];
    assignments?: any[];
    activeAssignment?: any;
    notes?: ClientNote[];
    addresses?: ClientAddress[];
    phones?: ClientPhone[];
  } | null>(null);

  // Client Notes inline/modal creation
  const [showAddClientNoteModal, setShowAddClientNoteModal] = useState(false);
  const [clientNoteText, setClientNoteText] = useState('');
  const [clientNoteCategory, setClientNoteCategory] = useState('GENERAL');
  const [savingClientNote, setSavingClientNote] = useState(false);
  const [clientNoteError, setClientNoteError] = useState<string | null>(null);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    role: 'HOME',
    formatted: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressActionError, setAddressActionError] = useState<string | null>(null);
  const [settingTreatmentId, setSettingTreatmentId] = useState<string | null>(null);
  const [archivingAddressId, setArchivingAddressId] = useState<string | null>(null);
  const [showAddPhoneModal, setShowAddPhoneModal] = useState(false);
  const [phoneForm, setPhoneForm] = useState({ label: 'Client', number: '' });
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneActionError, setPhoneActionError] = useState<string | null>(null);
  const [settingPrimaryPhoneId, setSettingPrimaryPhoneId] = useState<string | null>(null);
  const [archivingPhoneId, setArchivingPhoneId] = useState<string | null>(null);

  // Full Intake Modal & Packet Lifecycle
  const [showFullIntakeModal, setShowFullIntakeModal] = useState(false);
  const [editIntakeClientId, setEditIntakeClientId] = useState<string | null>(null);
  const [dispatchingPacket, setDispatchingPacket] = useState(false);
  const [syncingPacket, setSyncingPacket] = useState(false);
  const [remindingPacket, setRemindingPacket] = useState(false);
  const [completingIntake, setCompletingIntake] = useState(false);
  const [packetActionMsg, setPacketActionMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showSignVerifyModal, setShowSignVerifyModal] = useState(false);
  const [verifyRole, setVerifyRole] = useState<'GUARDIAN' | 'THERAPIST'>('GUARDIAN');
  const [verifyName, setVerifyName] = useState('');
  const [showIntakeLifecycle, setShowIntakeLifecycle] = useState(false);

  // Trigger opening full intake modal from sidebar Intake Packet
  useEffect(() => {
    if (openIntakeTrigger && openIntakeTrigger > 0) {
      setEditIntakeClientId(null);
      setShowFullIntakeModal(true);
    }
  }, [openIntakeTrigger]);

  // Trigger opening new client modal from sidebar + Client
  useEffect(() => {
    if (openNewClientTrigger && openNewClientTrigger > 0 && canManageClientOperations(userRole)) {
      setShowNewClientModal(true);
    }
  }, [openNewClientTrigger]);

  // New Client Modal (Quick Canonical Identity)
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    gender: 'Male',
    service_address: '',
    phone: '',
    email: '',
    is_ddd: true,
    primary_payer_name: 'Arizona Medicaid (Mercy Care)',
    policy_id: '',
    guardian_name: '',
    guardian_relationship: 'Parent / Legal Guardian',
    guardian_phone: '',
    guardian_email: ''
  });

  // Quick Auth Modal
  const [showNewAuthModal, setShowNewAuthModal] = useState(false);
  const [newAuthForm, setNewAuthForm] = useState({
    payer_name: 'Arizona Medicaid (Mercy Care)',
    auth_number: '',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
    service_code: '92507',
    total_units: 48,
    frequency: '2x/week'
  });

  // Client Document Upload Modal & State
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [uploadDocForm, setUploadDocForm] = useState({
    doc_type: 'EXTERNAL',
    title: '',
    document_date: new Date().toISOString().slice(0, 10),
    poc_end_date: '',
    notes: '',
    file_name: '',
    file_content: '',
    file_size: 0,
    mime_type: 'application/pdf'
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocError, setUploadDocError] = useState<string | null>(null);
  const [uploadDocSuccess, setUploadDocSuccess] = useState<string | null>(null);

  const handleDocFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setUploadDocForm(prev => ({
        ...prev,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || 'application/pdf',
        file_content: base64,
        title: prev.title || file.name.replace(/\.[^/.]+$/, "")
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleUploadDocumentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientDetail?.client?.id) return;
    setUploadingDoc(true);
    setUploadDocError(null);
    try {
      if (uploadDocForm.doc_type === 'PHYSICIAN_SIGNED_POC') {
        if (!uploadDocForm.document_date) {
          throw new Error('Document Date is required for Physician-Signed CPOC.');
        }
        if (!uploadDocForm.poc_end_date) {
          throw new Error('POC Expiration Date is required for Physician-Signed CPOC.');
        }
      }
      const res = await fetch('/api/clinical/docs/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientDetail.client.id,
          doc_type: uploadDocForm.doc_type,
          title: uploadDocForm.title || uploadDocForm.file_name,
          document_date: uploadDocForm.document_date,
          poc_end_date: uploadDocForm.poc_end_date || undefined,
          notes: uploadDocForm.notes,
          file_name: uploadDocForm.file_name,
          file_content: uploadDocForm.file_content,
          file_size: uploadDocForm.file_size,
          mime_type: uploadDocForm.mime_type
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to upload document');
      }
      setUploadDocSuccess('Document successfully uploaded and saved to client chart.');
      await fetchClientDetail(clientDetail.client.id);
      fetchClients();
      setTimeout(() => {
        setShowUploadDocModal(false);
        setUploadDocSuccess(null);
        setUploadDocForm({
          doc_type: 'EXTERNAL',
          title: '',
          document_date: new Date().toISOString().slice(0, 10),
          poc_end_date: clientDetail.client.poc_end_date || '',
          notes: '',
          file_name: '',
          file_content: '',
          file_size: 0,
          mime_type: 'application/pdf'
        });
      }, 1200);
    } catch (err: any) {
      setUploadDocError(err.message || 'Error uploading document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const isClinician = userRole === 'ASSISTANT';
  const canChangeClientStatus = canManageClientStatus(userRole);
  const canOperateClients = canManageClientOperations(userRole);
  const canManageCanonicalGoals = canEditCanonicalGoals({ role: userRole, providerCredential });
  const [goalEditor, setGoalEditor] = useState<null | { mode: 'add' } | { mode: 'modify'; goal: ClientGoal }>(null);
  const [goalForm, setGoalForm] = useState({ description: '', baseline: '', current: '0', target: '80', targetDate: '' });
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);
  const canEditRemoteSignature = canEditManualSoapApproval(userRole);
  const isClinicalStaff = userRole === 'ASSISTANT' || userRole === 'SUPERVISOR';
  const isReferralCoordinatorMobile = userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE';
  const isAdministrativeRole = userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE' || userRole === 'BILLING';
  const isOwner = userRole === 'OWNER' || userRole === 'ADMIN';
  const isOwnerOrRC = userRole === 'OWNER' || userRole === 'REFERRAL_COORDINATOR';
  const isSupervisor = userRole === 'SUPERVISOR';
  const canResolvePocConflict = userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'REFERRAL_COORDINATOR' || userRole === 'OFFICE';

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/clients');
      if (res.ok) {
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          if (Array.isArray(data)) {
            setClients(data);
          }
        } catch {
          console.error('API /api/clients returned non-JSON response (likely static hosting rewrite):', text.slice(0, 120));
        }
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (id: string) => {
    try {
      await fetchClientDetailIfSelected({
        requestedId: id,
        getSelectedId: () => selectedClientIdRef.current,
        apply: setClientDetail
      });
    } catch (err) {
      console.error('Error fetching client detail:', err);
    }
  };

  const openPocResolve = (client: Client) => {
    const startParts = splitPocDateParts(client.poc_start_date);
    const endParts = splitPocDateParts(client.poc_end_date);
    setPocResolveStart(toPocDateInputValue(startParts[0] || client.poc_start_date || ''));
    setPocResolveEnd(toPocDateInputValue(endParts[0] || client.poc_end_date || ''));
    setPocResolveStream('');
    setPocResolveError('');
    setPocResolveOpen(true);
  };

  const confirmPocResolve = async () => {
    if (!clientDetail?.client?.id) return;
    setPocResolveSaving(true);
    setPocResolveError('');
    try {
      const res = await fetch(`/api/clients/${clientDetail.client.id}/poc-conflict/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poc_start_date: pocResolveStart,
          poc_end_date: pocResolveEnd,
          authoritative_stream: pocResolveStream || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPocResolveError(data.error || 'Unable to resolve POC conflict.');
        return;
      }
      setPocResolveOpen(false);
      await fetchClientDetail(clientDetail.client.id);
      await fetchClients();
    } catch {
      setPocResolveError('Unable to resolve POC conflict.');
    } finally {
      setPocResolveSaving(false);
    }
  };

  const isTreatmentAddress = (addr: ClientAddress) =>
    Number(addr.is_default_treatment) === 1 || addr.is_default_treatment === true;

  const handleAddClientAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canOperateClients || !clientDetail?.client?.id) return;
    const formatted = addressForm.formatted.trim();
    if (!formatted) return;

    setSavingAddress(true);
    setAddressActionError(null);
    try {
      const res = await fetch(`/api/clients/${clientDetail.client.id}/addresses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: addressForm.label.trim() || 'Address',
          role: addressForm.role,
          formatted
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAddressActionError(data.error || 'Unable to save address.');
        return;
      }
      setShowAddAddressModal(false);
      setAddressForm({ label: 'Home', role: 'HOME', formatted: '' });
      await fetchClientDetail(clientDetail.client.id);
    } catch (err) {
      console.error('Error adding client address:', err);
      setAddressActionError('Unable to save address.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSetTreatmentAddress = async (addressId: string) => {
    if (!canOperateClients || !clientDetail?.client?.id) return;
    setSettingTreatmentId(addressId);
    setAddressActionError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientDetail.client.id}/addresses/${addressId}/set-treatment`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAddressActionError(data.error || 'Unable to set treatment address.');
        return;
      }
      await fetchClientDetail(clientDetail.client.id);
      fetchClients();
    } catch (err) {
      console.error('Error setting treatment address:', err);
      setAddressActionError('Unable to set treatment address.');
    } finally {
      setSettingTreatmentId(null);
    }
  };

  const handleArchiveClientAddress = async (addressId: string) => {
    if (!canOperateClients || !clientDetail?.client?.id) return;
    setArchivingAddressId(addressId);
    setAddressActionError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientDetail.client.id}/addresses/${addressId}/archive`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAddressActionError(data.error || 'Unable to delete address.');
        return;
      }
      await fetchClientDetail(clientDetail.client.id);
    } catch (err) {
      console.error('Error archiving client address:', err);
      setAddressActionError('Unable to delete address.');
    } finally {
      setArchivingAddressId(null);
    }
  };

  const isPrimaryPhone = (phone: ClientPhone) =>
    Number(phone.is_default) === 1 || phone.is_default === true;

  const handleAddClientPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientDetail?.client?.id) return;
    const number = phoneForm.number.trim();
    if (!number) return;

    setSavingPhone(true);
    setPhoneActionError(null);
    try {
      const res = await fetch(`/api/clients/${clientDetail.client.id}/phones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: phoneForm.label,
          number
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhoneActionError(data.error || 'Unable to save phone.');
        return;
      }
      setShowAddPhoneModal(false);
      setPhoneForm({ label: 'Client', number: '' });
      await fetchClientDetail(clientDetail.client.id);
    } catch (err) {
      console.error('Error adding client phone:', err);
      setPhoneActionError('Unable to save phone.');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSetPrimaryPhone = async (phoneId: string) => {
    if (!clientDetail?.client?.id) return;
    setSettingPrimaryPhoneId(phoneId);
    setPhoneActionError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientDetail.client.id}/phones/${phoneId}/set-primary`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhoneActionError(data.error || 'Unable to set primary phone.');
        return;
      }
      await fetchClientDetail(clientDetail.client.id);
      fetchClients();
    } catch (err) {
      console.error('Error setting primary phone:', err);
      setPhoneActionError('Unable to set primary phone.');
    } finally {
      setSettingPrimaryPhoneId(null);
    }
  };

  const handleArchiveClientPhone = async (phoneId: string) => {
    if (!clientDetail?.client?.id) return;
    setArchivingPhoneId(phoneId);
    setPhoneActionError(null);
    try {
      const res = await fetch(
        `/api/clients/${clientDetail.client.id}/phones/${phoneId}/archive`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhoneActionError(data.error || 'Unable to delete phone.');
        return;
      }
      await fetchClientDetail(clientDetail.client.id);
    } catch (err) {
      console.error('Error archiving client phone:', err);
      setPhoneActionError('Unable to delete phone.');
    } finally {
      setArchivingPhoneId(null);
    }
  };

  const handleSaveClientNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientDetail?.client?.id) return;
    const trimmed = clientNoteText.trim();
    if (!trimmed) return;

    setSavingClientNote(true);
    setClientNoteError(null);

    try {
      const res = await fetch(`/api/clients/${clientDetail.client.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: trimmed,
          category: clientNoteCategory || 'GENERAL',
          source: 'CLIENT_OVERVIEW'
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: `Request failed with status ${res.status}` }));
        throw new Error(errData.error || `Failed to save note (HTTP ${res.status})`);
      }

      const createdNote: ClientNote = await res.json();
      setClientDetail(prev => {
        if (!prev) return prev;
        const currentNotes = prev.notes || [];
        return {
          ...prev,
          notes: [createdNote, ...currentNotes.filter(n => n.id !== createdNote.id)]
        };
      });

      setClientNoteText('');
      setClientNoteCategory('GENERAL');
      setShowAddClientNoteModal(false);
    } catch (err: any) {
      setClientNoteError(err.message || 'Failed to save note.');
    } finally {
      setSavingClientNote(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await fetch('/api/providers');
      if (res.ok) setAllProviders(await res.json());
    } catch (err) {
      console.error('Error fetching providers:', err);
    }
  };

  const openGoalEditor = (editor: null | { mode: 'add' } | { mode: 'modify'; goal: ClientGoal }) => {
    setGoalError(null);
    setGoalEditor(editor);
    if (!editor) return;
    if (editor.mode === 'add') {
      setGoalForm({ description: '', baseline: '', current: '0', target: '80', targetDate: '' });
      return;
    }
    setGoalForm({
      description: editor.goal.description || '',
      baseline: editor.goal.baseline_performance || '',
      current: String(editor.goal.current_percentage ?? 0),
      target: String(editor.goal.target_percentage ?? 80),
      targetDate: editor.goal.target_date || ''
    });
  };

  const saveCanonicalGoal = async (action: 'ADD' | 'MODIFY' | 'MET' | 'DISCONTINUE', goalId?: string) => {
    const clientId = clientDetail?.client?.id;
    if (!clientId || goalSaving) return;
    setGoalSaving(true);
    setGoalError(null);
    try {
      const payload = action === 'MET' || action === 'DISCONTINUE'
        ? { action }
        : {
            action: action === 'MODIFY' ? 'MODIFY' : undefined,
            description: goalForm.description,
            baseline_performance: goalForm.baseline,
            current_percentage: Number(goalForm.current || 0),
            target_percentage: Number(goalForm.target || 80),
            target_date: goalForm.targetDate
          };
      const res = await fetch(goalId ? `/api/clients/${clientId}/goals/${goalId}` : `/api/clients/${clientId}/goals`, {
        method: goalId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'ADD' ? {
          description: goalForm.description,
          baseline_performance: goalForm.baseline,
          current_percentage: Number(goalForm.current || 0),
          target_percentage: Number(goalForm.target || 80),
          target_date: goalForm.targetDate
        } : payload)
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setGoalError(body.error || 'Canonical goal was not saved.');
        return;
      }
      openGoalEditor(null);
      await fetchClientDetail(clientId);
    } catch (err: any) {
      setGoalError(err?.message || 'Canonical goal was not saved.');
    } finally {
      setGoalSaving(false);
    }
  };

  const handleToggleClientStatus = async (newStatus: 'ACTIVE' | 'INACTIVE', targetClientId?: string) => {
    const id = targetClientId || clientDetail?.client?.id;
    if (!canManageClientStatus(userRole) || !id || statusUpdating) return;
    setStatusUpdating(true);
    try {
      const res = await fetch(`/api/clients/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setClientDetail(prev => (prev && prev.client.id === id) ? {
          ...prev,
          client: { ...prev.client, status: newStatus }
        } : prev);
        await fetchClients();
      } else {
        const err = await res.json();
        console.error(err.error || 'Failed to update client status');
      }
    } catch (err: any) {
      console.error(err.message || 'Network error updating client status');
    } finally {
      setStatusUpdating(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchProviders();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientDetail(selectedClientId);
    } else {
      setClientDetail(null);
    }
  }, [selectedClientId]);

  const handleOpenAssignModal = () => {
    if (!canOperateClients) return;
    const active = clientDetail?.activeAssignment;
    setAssignForm({
      provider_id: active?.provider_id || '',
      supervisor_id: active?.supervisor_id || '',
      expected_frequency: active?.expected_frequency || '2x/week',
      expected_duration_minutes: active?.expected_duration_minutes || 60,
      notes: active?.notes || ''
    });
    setShowAssignModal(true);
  };

  const handleSaveAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canOperateClients || !selectedClientId || !assignForm.provider_id) return;
    try {
      const res = await fetch(`/api/clients/${selectedClientId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignForm)
      });
      if (res.ok) {
        setShowAssignModal(false);
        await fetchClientDetail(selectedClientId);
      }
    } catch (err) {
      console.error('Error saving assignment:', err);
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageClientOperations(userRole) || !newClientForm.first_name || !newClientForm.last_name || !newClientForm.dob) return;

    try {
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClientForm)
      });
      if (res.ok) {
        const data = await res.json();
        setShowNewClientModal(false);
        setNewClientForm({
          first_name: '',
          last_name: '',
          dob: '',
          gender: 'Male',
          service_address: '',
          phone: '',
          email: '',
          primary_payer_name: 'Arizona Medicaid (Mercy Care)',
          policy_id: '',
          guardian_name: '',
          guardian_relationship: 'Parent / Legal Guardian',
          guardian_phone: '',
          guardian_email: ''
        });
        await fetchClients();
        onSelectClient(data.clientId);
      }
    } catch (err) {
      console.error('Create client failed:', err);
    }
  };

  const handleDispatchIntakePacket = async (clientId: string) => {
    if (!clientDetail) return;
    setPacketActionMsg(null);

    // BLOCK DISPATCH IF UNASSIGNED THERAPIST
    if (!clientDetail.client.assigned_provider_id) {
      setPacketActionMsg({
        text: 'Cannot dispatch packet: An assigned treating therapist is required. Please assign a therapist from the provider roster before sending.',
        type: 'error'
      });
      return;
    }

    setDispatchingPacket(true);
    try {
      // Acquire server-minted single-use eSign intent
      const intentRes = await fetch('/api/communications/esign/intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          recipientEmail: clientDetail.client.guardian_email,
          providerId: clientDetail.client.assigned_provider_id,
          dispatchType: 'INTAKE_PACKET'
        })
      });
      const intentData = await intentRes.json();
      if (!intentRes.ok || !intentData.intentId) {
        throw new Error(intentData.error || 'Failed to acquire eSign dispatch authorization');
      }

      const res = await fetch(`/api/clients/${clientId}/dispatch-intake-packet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Initiation-Source': 'MANUAL_UI'
        },
        body: JSON.stringify({
          intentId: intentData.intentId
        })
      });
      const data = await res.json();
      if (!res.ok || data.success !== true || !data.envelopeId) {
        throw new Error(data.error || data.failureReason || 'Failed to dispatch intake packet: No envelope was created');
      }
      setPacketActionMsg({
        text: `Intake Packet dispatched via SignWell to Guardian & Treating Therapist! Envelope ID: ${data.envelopeId}`,
        type: 'success'
      });
      await fetchClientDetail(clientId);
      await fetchClients();
    } catch (err: any) {
      setPacketActionMsg({ text: err.message || 'Error dispatching packet', type: 'error' });
    } finally {
      setDispatchingPacket(false);
    }
  };

  const handleSyncIntakePacket = async (clientId: string) => {
    setPacketActionMsg(null);
    setSyncingPacket(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/intake-packet/sync`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to sync intake status');
      }
      setPacketActionMsg({
        text: `SignWell status synchronized: ${data.client?.intake_status || data.syncResult?.status || 'Updated'}`,
        type: 'success'
      });
      await fetchClientDetail(clientId);
      await fetchClients();
    } catch (err: any) {
      setPacketActionMsg({ text: err.message || 'Error syncing status', type: 'error' });
    } finally {
      setSyncingPacket(false);
    }
  };

  const handleRemindIntakePacket = async (clientId: string) => {
    setPacketActionMsg(null);
    setRemindingPacket(true);
    try {
      // Acquire server-minted single-use eSign intent
      const intentRes = await fetch('/api/communications/esign/intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          dispatchType: 'REMINDER'
        })
      });
      const intentData = await intentRes.json();
      if (!intentRes.ok || !intentData.intentId) {
        throw new Error(intentData.error || 'Failed to acquire eSign dispatch authorization');
      }

      const res = await fetch(`/api/clients/${clientId}/intake-packet/remind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Initiation-Source': 'MANUAL_UI'
        },
        body: JSON.stringify({
          intentId: intentData.intentId
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to send SignWell reminder');
      }
      setPacketActionMsg({
        text: data.message || 'SignWell reminder sent to unsigned recipient(s).',
        type: 'success'
      });
      await fetchClientDetail(clientId);
    } catch (err: any) {
      setPacketActionMsg({ text: err.message || 'Error sending reminder', type: 'error' });
    } finally {
      setRemindingPacket(false);
    }
  };

  const handleRecordSignature = async (clientId: string, role: 'GUARDIAN' | 'THERAPIST', name: string) => {
    setPacketActionMsg(null);
    try {
      const res = await fetch(`/api/clients/${clientId}/intake-packet/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signer_role: role, signer_name: name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to record signature');
      setShowSignVerifyModal(false);
      setPacketActionMsg({
        text: `Recorded ${role} signature. Intake status: ${data.intake_status}`,
        type: 'success'
      });
      await fetchClientDetail(clientId);
      await fetchClients();
    } catch (err: any) {
      setPacketActionMsg({ text: err.message || 'Error recording signature', type: 'error' });
    }
  };

  const handleCompleteIntake = async (clientId: string) => {
    setPacketActionMsg(null);
    setCompletingIntake(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/intake-packet/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'COMPLETE' })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Cannot complete intake without both signatures.');
      }
      setPacketActionMsg({
        text: 'Intake verified and marked Complete! Canonical client status updated to ACTIVE.',
        type: 'success'
      });
      await fetchClientDetail(clientId);
      await fetchClients();
    } catch (err: any) {
      setPacketActionMsg({ text: err.message || 'Intake completion blocked', type: 'error' });
    } finally {
      setCompletingIntake(false);
    }
  };

  const handleCreateAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId || !newAuthForm.auth_number) return;

    try {
      const res = await fetch('/api/authorizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newAuthForm,
          client_id: selectedClientId
        })
      });
      if (res.ok) {
        setShowNewAuthModal(false);
        setNewAuthForm({
          payer_name: 'Arizona Medicaid (Mercy Care)',
          auth_number: '',
          start_date: new Date().toISOString().slice(0, 10),
          end_date: new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
          service_code: '92507',
          total_units: 48,
          frequency: '2x/week'
        });
        fetchClientDetail(selectedClientId);
        fetchClients();
      }
    } catch (err) {
      console.error('Create auth failed:', err);
    }
  };

  const activeCount = React.useMemo(() => clients.filter(c => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE').length, [clients]);
  const pendingIntakeCount = React.useMemo(() => clients.filter(c => (c.status || '').toUpperCase() === 'PENDING_INTAKE').length, [clients]);
  const inactiveCount = React.useMemo(() => clients.filter(c => {
    const s = (c.status || 'ACTIVE').toUpperCase();
    return s === 'INACTIVE' || s === 'DISCHARGED';
  }).length, [clients]);
  const allCount = clients.length;

  const filteredClients = React.useMemo(() => {
    const list = clients.filter(c => {
      const s = (c.status || 'ACTIVE').toUpperCase();
      const query = search.toLowerCase().trim();

      // If search query is present, search across all statuses so clients in any lifecycle state (e.g., Mickey Mouse, Marg Simpson) can be found
      if (query) {
        return `${c.first_name} ${c.last_name} ${c.primary_payer_name || ''} ${c.id} ${c.ahcccs_id || ''} ${c.wellsky_caseno || ''} ${c.dob || ''}`
          .toLowerCase()
          .includes(query);
      }

      if (statusFilter === 'ACTIVE') return s === 'ACTIVE';
      if (statusFilter === 'PENDING_INTAKE') return s === 'PENDING_INTAKE';
      if (statusFilter === 'INACTIVE') return s === 'INACTIVE' || s === 'DISCHARGED';
      return true;
    });

    return [...list].sort((a, b) => {
      const lastA = a.last_name || '';
      const lastB = b.last_name || '';
      const cmpLast = lastA.localeCompare(lastB, undefined, { sensitivity: 'base' });
      if (cmpLast !== 0) return cmpLast;
      const firstA = a.first_name || '';
      const firstB = b.first_name || '';
      return firstA.localeCompare(firstB, undefined, { sensitivity: 'base' });
    });
  }, [clients, search, statusFilter]);

  return (
    <div className="space-y-6">
      {/* Referral Coordinator Mobile Header: Canonical Steel-to-Navy Shell (Preserved on roster view, hidden inside client detail flow) */}
      {isReferralCoordinatorMobile && !selectedClientId && (
        <div className="md:hidden relative overflow-hidden rounded-2xl border mobile-header-shell px-3.5 py-3 space-y-2.5 w-full">
          <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />

          {/* Centered Title */}
          <div className="relative z-10 flex items-center justify-center space-x-2">
            <h1 className="text-base sm:text-lg font-bold text-white tracking-tight text-center">Client Charts</h1>
          </div>
          
          {/* Mobile Switch: Directory | POC & Auth Expirations */}
          <div className="relative z-10 flex items-center bg-slate-100 border border-slate-200/90 rounded-xl p-1 gap-1 w-full shadow-2xs">
            <button
              type="button"
              onClick={() => {
                setViewMode('roster');
              }}
              className={`flex-1 h-8 px-2.5 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                viewMode === 'roster'
                  ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                  : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
              }`}
            >
              <Users className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'roster' ? 'text-white' : 'text-slate-600'}`} />
              <span>Directory</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('expirations');
              }}
              className={`flex-1 h-8 px-2.5 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                viewMode === 'expirations'
                  ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                  : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
              }`}
            >
              <Clock className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'expirations' ? 'text-white' : 'text-slate-600'}`} />
              <span>POC & Auth Expirations</span>
            </button>
          </div>

          {/* RC Operational Shortcuts: [ Intake ] [ Add Client ] */}
          <div className="relative z-10 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => {
                setEditIntakeClientId(null);
                setShowFullIntakeModal(true);
              }}
              className="flex-1 max-w-[170px] h-8 px-3 rounded-xl bg-gradient-to-r from-emerald-600/90 via-emerald-600 to-teal-700/90 hover:from-emerald-500 hover:via-emerald-500 hover:to-teal-600 text-white border border-emerald-400/50 hover:border-emerald-300/80 shadow-[0_0_14px_rgba(16,185,129,0.22),0_2px_6px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.3)] text-xs font-bold transition-all inline-flex items-center justify-center space-x-1.5 active:translate-y-[0.5px] cursor-pointer"
              title="Full Allegiant Intake Packet & Service Agreement Workflow"
              id="mobile-rc-intake-shortcut-btn"
            >
              <FileText className="w-3.5 h-3.5 shrink-0 text-emerald-100" />
              <span>Intake</span>
            </button>
            {canOperateClients && (
            <button
              type="button"
              onClick={() => setShowNewClientModal(true)}
              className="flex-1 max-w-[170px] h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-900 border border-white/80 shadow-xs text-xs font-semibold transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer"
              title="Add New Client"
              id="mobile-rc-add-client-shortcut-btn"
            >
              <Plus className="w-3.5 h-3.5 shrink-0 stroke-[2.5]" />
              <span>Add Client</span>
            </button>
            )}
          </div>
        </div>
      )}

      {/* Workspace Header (Desktop/Tablet for all roles, and Mobile for non-RC roles when list is active) */}
      <div className={`${isReferralCoordinatorMobile ? 'hidden md:block' : selectedClientId ? 'hidden lg:block' : ''} relative overflow-hidden rounded-2xl border mobile-header-shell md:border-slate-700/80 md:desktop-header-shell px-4 py-3 sm:px-5 sm:py-3.5 md:px-6 md:py-4 shadow-xl md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]`}>
          {/* Subtle specular top hairline on desktop */}
          <div className="hidden md:block absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/30 to-transparent pointer-events-none" />
          <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-3.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              {/* Mobile Centered Title Cluster (md:hidden) — No leading icon, centered title + centered Unified Roster badge */}
              <div className="md:hidden flex flex-col items-center justify-center text-center w-full space-y-1">
                <h1 className="text-xl font-bold text-white tracking-tight">
                  Client Charts
                </h1>
                <span className="text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-slate-800/90 text-slate-200 border border-slate-500/45 shadow-xs inline-block">
                  Unified Roster
                </span>
              </div>

              {/* Desktop/Tablet Title Cluster (hidden md:flex) - HARD FROZEN */}
              <div className="hidden md:flex items-center space-x-2.5">
                <IconBadge icon={Users} variant="sky" size="md" />
                <h1 className="text-xl font-bold text-white tracking-tight flex items-center space-x-2">
                  <span>Client Charts</span>
                  <span className="text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-slate-800/90 text-slate-200 border border-slate-500/45 shadow-xs">
                    Unified Roster
                  </span>
                </h1>
              </div>

              {/* Desktop View mode toggle: Client Directory vs POC & Auth Expirations */}
              <div className="hidden md:flex items-center rounded-xl p-1 gap-1 bg-slate-100 border border-slate-200/90">
                <button
                  onClick={() => setViewMode('roster')}
                  className={`h-8 px-3 text-xs font-semibold rounded-lg transition-all inline-flex items-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    viewMode === 'roster'
                      ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                      : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                  }`}
                >
                  <Users className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'roster' ? 'text-white' : 'text-slate-600'}`} />
                  <span>Client Directory</span>
                </button>
                <button
                  onClick={() => setViewMode('expirations')}
                  className={`h-8 px-3 text-xs font-semibold rounded-lg transition-all inline-flex items-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                    viewMode === 'expirations'
                      ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                      : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                  }`}
                >
                  <Clock className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'expirations' ? 'text-white' : 'text-slate-600'}`} />
                  <span>POC & Auth Expirations</span>
                </button>
              </div>

              {/* Mobile Navigation Controls (md:hidden) */}
              <div className="flex md:hidden flex-col gap-2 w-full">
                {/* Row 1: Primary Navigation Controls */}
                <div className="flex items-center bg-slate-100 border border-slate-200/90 rounded-xl p-1 gap-1 w-full shadow-2xs">
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('roster');
                    }}
                    className={`flex-1 h-8 px-2 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                      viewMode === 'roster'
                        ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                        : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                    }`}
                  >
                    <Users className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'roster' ? 'text-white' : 'text-slate-600'}`} />
                    <span>Directory</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('expirations');
                    }}
                    className={`flex-1 h-8 px-2 text-xs font-semibold rounded-lg transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70 ${
                      viewMode === 'expirations'
                        ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-xs'
                        : 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                    }`}
                  >
                    <Clock className={`w-3.5 h-3.5 shrink-0 ${viewMode === 'expirations' ? 'text-white' : 'text-slate-600'}`} />
                    <span>Expirations</span>
                  </button>

                  {/* Owner Mobile Intake Shortcut in Row 1 */}
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditIntakeClientId(null);
                        setShowFullIntakeModal(true);
                      }}
                      className="flex-1 h-8 px-2 text-xs font-bold rounded-lg bg-gradient-to-r from-emerald-600/90 via-emerald-600 to-teal-700/90 hover:from-emerald-500 text-white border border-emerald-400/50 shadow-[0_0_10px_rgba(16,185,129,0.2),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer active:translate-y-[0.5px]"
                      title="Intake (Full Packet & Service Agreement)"
                      id="mobile-owner-intake-btn"
                    >
                      <FileText className="w-3.5 h-3.5 shrink-0 text-emerald-100" />
                      <span>Intake</span>
                    </button>
                  )}
                </div>

                {/* Row 2 (Owner Mobile Only): Normal Add Client action beneath navigation row */}
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => setShowNewClientModal(true)}
                    className="w-full h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-900 border border-white/80 shadow-xs text-xs font-semibold transition-all inline-flex items-center justify-center space-x-1.5 cursor-pointer"
                    title="Add New Client"
                    id="mobile-owner-add-client-btn"
                  >
                    <Plus className="w-3.5 h-3.5 shrink-0 stroke-[2.5]" />
                    <span>Add Client</span>
                  </button>
                )}
              </div>
            </div>

            {/* Desktop/Tablet Client Action (Hard frozen, hidden on mobile) */}
            {(isOwner || canOperateClients) && (
              <div className="hidden md:flex items-center space-x-2.5">
                {isOwner && (
                  <button
                    onClick={() => {
                      setEditIntakeClientId(null);
                      setShowFullIntakeModal(true);
                    }}
                    className="h-8 px-3 rounded-xl bg-gradient-to-r from-emerald-600/90 via-emerald-600 to-teal-700/90 hover:from-emerald-500 hover:via-emerald-500 hover:to-teal-600 text-white border border-emerald-400/50 hover:border-emerald-300/80 shadow-[0_0_14px_rgba(16,185,129,0.22),0_2px_6px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.3)] text-xs font-bold transition-all inline-flex items-center space-x-1.5 active:translate-y-[0.5px] cursor-pointer"
                    title="Intake (Full Packet & Service Agreement)"
                    id="desktop-owner-intake-btn"
                  >
                    <FileText className="w-3.5 h-3.5 text-emerald-100" />
                    <span>Intake</span>
                  </button>
                )}
                {canOperateClients && (
                <button
                  onClick={() => setShowNewClientModal(true)}
                  className="h-8 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-900 text-xs font-semibold transition-all inline-flex items-center space-x-1.5 border border-white/80 shadow-xs cursor-pointer"
                  title="Add New Client"
                  id="desktop-add-client-btn"
                >
                  <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>Add Client</span>
                </button>
                )}
              </div>
            )}
          </div>
        </div>

      {viewMode === 'expirations' ? (
        <PocAuthExpirationsView
          userRole={userRole}
          initialFilter={expirationsFilter}
          filterTrigger={filterTrigger}
          onSelectClient={(id) => {
            setLastSelectedMode('expirations');
            onSelectClient(id);
            setViewMode('roster');
          }}
        />
      ) : (
      /* Main View: Split view if a client is selected, or full table */
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Clients List Column - On mobile, hidden when client chart detail is open */}
        <div className={selectedClientId ? 'hidden lg:block lg:col-span-4 space-y-3 relative z-10' : 'lg:col-span-12 space-y-3 relative z-10'}>
          {/* Consolidated Filter & Scoped Arc Search Row */}
          <div className={`flex flex-col ${selectedClientId ? 'gap-2' : 'gap-2.5 sm:flex-row sm:items-center justify-between'}`}>
            {/* Simple Scoped Search Surface (Deterministic Search) */}
            <div className={`relative ${selectedClientId ? 'w-full' : 'order-1 sm:order-2 flex-1 min-w-[240px]'}`}>
              <div className="relative flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50/80 border border-slate-200/90 shadow-2xs transition-all focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300 focus-within:bg-white">
                <div className="flex items-center pl-0.5 shrink-0" title="Bloom Search">
                  <img
                    src="/brand/canonical/bloom-mark-micro.svg"
                    alt=""
                    className="w-4 h-4 shrink-0 object-contain"
                  />
                </div>

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by client name..."
                  className="w-full bg-transparent text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none tracking-tight font-medium"
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors shrink-0 cursor-pointer"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter Tabs */}
            <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between sm:justify-start gap-2 ${selectedClientId ? 'w-full' : 'order-2 sm:order-1 w-full sm:w-auto'}`}>
              {/* Desktop / Tablet Status Filter Tabs (sm and up) */}
              <div className={`hidden sm:grid sm:grid-cols-4 gap-1.5 p-1 rounded-xl text-xs ${isClinicalStaff ? 'bg-slate-100 border border-slate-200/90' : 'pulse-control-shell'} ${selectedClientId ? 'w-full' : 'w-full sm:w-auto'}`}>
                <button
                  type="button"
                  onClick={() => setStatusFilter('ACTIVE')}
                  className={`w-full h-9 min-h-[36px] px-1.5 rounded-lg text-xs font-semibold transition-all text-center justify-center flex items-center cursor-pointer ${
                    statusFilter === 'ACTIVE'
                      ? isOwnerOrRC
                        ? 'bg-slate-800 text-white font-bold border border-slate-700 shadow-xs'
                        : isClinicalStaff
                        ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-sm'
                        : 'bg-gradient-to-b from-[#0284c7] via-[#0369a1] to-[#1e40af] text-white font-bold border border-cyan-300/85 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.48),0_2px_8px_rgba(2,132,199,0.45),0_0_12px_rgba(6,182,212,0.25)] ring-1 ring-cyan-400/40'
                      : isClinicalStaff
                      ? 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      : 'bg-gradient-to-b from-[#0b1526]/90 via-[#08101e]/90 to-[#050a14]/90 text-slate-300/90 hover:text-sky-100 border border-sky-950/70 hover:border-sky-800/60 shadow-[inset_0_1px_0_0_rgba(56,189,248,0.05)] hover:shadow-[0_0_8px_rgba(14,165,233,0.12)]'
                  }`}
                >
                  <span className="leading-tight text-center">Active ({activeCount})</span>
                </button>
                {pendingIntakeCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setStatusFilter('PENDING_INTAKE')}
                    className={`w-full h-9 min-h-[36px] px-1.5 rounded-lg text-xs font-semibold transition-all text-center justify-center flex items-center cursor-pointer ${
                      statusFilter === 'PENDING_INTAKE'
                        ? isClinicalStaff
                          ? 'bg-amber-100 text-amber-900 font-bold border border-amber-300 shadow-xs'
                          : 'bg-gradient-to-b from-[#0e1726] via-[#09111c] to-[#060a12] text-amber-300 font-bold border border-amber-400/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15),0_2px_8px_rgba(0,0,0,0.5),0_0_12px_rgba(245,158,11,0.25)] ring-1 ring-amber-400/40'
                        : isClinicalStaff
                        ? 'bg-white text-amber-800 hover:text-amber-950 border border-amber-200/90 hover:bg-amber-50/60'
                        : 'bg-gradient-to-b from-[#0b1526]/90 via-[#08101e]/90 to-[#050a14]/90 text-amber-300 hover:text-amber-200 font-semibold border border-sky-900/45 hover:border-sky-700/50 shadow-[inset_0_1px_0_0_rgba(56,189,248,0.06)] hover:shadow-[0_0_8px_rgba(14,165,233,0.12)]'
                    }`}
                  >
                    <span className="leading-tight text-center">Pending Intake ({pendingIntakeCount})</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setStatusFilter('PENDING_INTAKE')}
                    className={`w-full h-9 min-h-[36px] px-1.5 rounded-lg text-xs font-semibold transition-all text-center justify-center flex items-center cursor-pointer ${
                      statusFilter === 'PENDING_INTAKE'
                        ? isOwnerOrRC
                          ? 'bg-slate-800 text-white font-bold border border-slate-700 shadow-xs'
                          : isClinicalStaff
                          ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-sm'
                          : 'bg-gradient-to-b from-[#0e1726] via-[#09111c] to-[#060a12] text-amber-300 font-bold border border-amber-400/80 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.15),0_2px_8px_rgba(0,0,0,0.5),0_0_12px_rgba(245,158,11,0.25)] ring-1 ring-amber-400/40'
                        : isClinicalStaff
                        ? 'bg-white text-slate-500 hover:text-slate-800 border border-slate-200/90 hover:bg-slate-50'
                        : 'bg-gradient-to-b from-[#0b1526]/90 via-[#08101e]/90 to-[#050a14]/90 text-slate-400 hover:text-amber-200 border border-sky-900/45 hover:border-sky-700/50 shadow-[inset_0_1px_0_0_rgba(56,189,248,0.06)]'
                    }`}
                  >
                    <span className="leading-tight text-center">Pending Intake (0)</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setStatusFilter('INACTIVE')}
                  className={`w-full h-9 min-h-[36px] px-1.5 rounded-lg text-xs font-semibold transition-all text-center justify-center flex items-center cursor-pointer ${
                    statusFilter === 'INACTIVE'
                      ? isOwnerOrRC
                        ? 'bg-slate-800 text-white font-bold border border-slate-700 shadow-xs'
                        : isClinicalStaff
                        ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-sm'
                        : 'bg-gradient-to-b from-[#161f2e] via-[#101724] to-[#090e17] text-white font-bold border border-slate-600/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_2px_8px_rgba(0,0,0,0.5)]'
                      : isClinicalStaff
                      ? 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      : 'bg-gradient-to-b from-[#0b1526]/90 via-[#08101e]/90 to-[#050a14]/90 text-slate-300/90 hover:text-sky-100 border border-sky-950/70 hover:border-sky-800/60 shadow-[inset_0_1px_0_0_rgba(56,189,248,0.05)] hover:shadow-[0_0_8px_rgba(14,165,233,0.12)]'
                  }`}
                >
                  <span className="leading-tight text-center">Inactive ({inactiveCount})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className={`w-full h-9 min-h-[36px] px-1.5 rounded-lg text-xs font-semibold transition-all text-center justify-center flex items-center cursor-pointer ${
                    statusFilter === 'ALL'
                      ? isOwnerOrRC
                        ? 'bg-slate-800 text-white font-bold border border-slate-700 shadow-xs'
                        : isClinicalStaff
                        ? 'bg-slate-800 text-white font-semibold border border-slate-700 shadow-sm'
                        : 'bg-gradient-to-b from-[#161f2e] via-[#101724] to-[#090e17] text-white font-bold border border-slate-600/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_2px_8px_rgba(0,0,0,0.5)]'
                      : isClinicalStaff
                      ? 'bg-white text-slate-700 hover:text-slate-950 border border-slate-200/90 hover:bg-slate-50'
                      : 'bg-gradient-to-b from-[#0b1526]/90 via-[#08101e]/90 to-[#050a14]/90 text-slate-300/90 hover:text-sky-100 border border-sky-950/70 hover:border-sky-800/60 shadow-[inset_0_1px_0_0_rgba(56,189,248,0.05)] hover:shadow-[0_0_8px_rgba(14,165,233,0.12)]'
                  }`}
                >
                  <span className="leading-tight text-center">All ({allCount})</span>
                </button>
              </div>

              {/* Mobile 2×2 filter pills: Active | Pending Intake / Inactive | All */}
              <div className="grid sm:hidden grid-cols-2 gap-2 w-full mobile-ops-shell p-1.5 rounded-xl">
                <button
                  type="button"
                  onClick={() => setStatusFilter('ACTIVE')}
                  className={`min-h-[44px] py-2.5 px-3 rounded-lg transition-all text-center flex flex-col items-center justify-center cursor-pointer ${
                    statusFilter === 'ACTIVE'
                      ? 'bg-slate-900 text-white font-bold border border-slate-800 shadow-xs'
                      : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/80 shadow-2xs'
                  }`}
                >
                  <span className="text-[12px] font-semibold tracking-tight leading-tight text-center">Active</span>
                  <span className={`text-[13px] font-mono leading-none mt-1 ${statusFilter === 'ACTIVE' ? 'text-slate-200 font-extrabold' : 'text-slate-500 font-bold'}`}>
                    {activeCount}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter('PENDING_INTAKE')}
                  className={`min-h-[44px] py-2.5 px-3 rounded-lg transition-all text-center flex flex-col items-center justify-center cursor-pointer ${
                    statusFilter === 'PENDING_INTAKE'
                      ? 'bg-amber-50 text-amber-900 font-bold border border-amber-300 shadow-xs'
                      : 'bg-white hover:bg-slate-50 text-amber-800 hover:text-amber-900 border border-slate-200/80 shadow-2xs'
                  }`}
                >
                  <span className="text-[12px] font-semibold tracking-tight leading-tight text-center">Pending Intake</span>
                  <span className={`text-[13px] font-mono leading-none mt-1 ${statusFilter === 'PENDING_INTAKE' ? 'text-amber-800 font-extrabold' : 'text-amber-600 font-bold'}`}>
                    {pendingIntakeCount}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter('INACTIVE')}
                  className={`min-h-[44px] py-2.5 px-3 rounded-lg transition-all text-center flex flex-col items-center justify-center cursor-pointer ${
                    statusFilter === 'INACTIVE'
                      ? 'bg-slate-900 text-white font-bold border border-slate-800 shadow-xs'
                      : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/80 shadow-2xs'
                  }`}
                >
                  <span className="text-[12px] font-semibold tracking-tight leading-tight text-center">Inactive</span>
                  <span className={`text-[13px] font-mono leading-none mt-1 ${statusFilter === 'INACTIVE' ? 'text-slate-200 font-extrabold' : 'text-slate-500 font-bold'}`}>
                    {inactiveCount}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className={`min-h-[44px] py-2.5 px-3 rounded-lg transition-all text-center flex flex-col items-center justify-center cursor-pointer ${
                    statusFilter === 'ALL'
                      ? 'bg-slate-900 text-white font-bold border border-slate-800 shadow-xs'
                      : 'bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/80 shadow-2xs'
                  }`}
                >
                  <span className="text-[12px] font-semibold tracking-tight leading-tight text-center">All</span>
                  <span className={`text-[13px] font-mono leading-none mt-1 ${statusFilter === 'ALL' ? 'text-slate-200 font-extrabold' : 'text-slate-500 font-bold'}`}>
                    {allCount}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Roster Container - Established Arc Navy / Slate Depth */}
          <div className={`relative rounded-2xl overflow-hidden border shadow-xl ${
            isClinicalStaff
              ? 'md:bg-white md:border-slate-200/90 md:shadow-xs mobile-ops-shell'
              : 'mobile-ops-shell md:bg-gradient-to-b md:from-[#18263c] md:via-[#141f32] md:to-[#101929] border-slate-700/80 md:border-slate-700/70 shadow-black/40'
          }`}>
            {/* Subtle cool silver / blue hairline shimmer */}
            {!isClinicalStaff && (
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/25 to-transparent pointer-events-none" />
            )}

            {loading ? (
              <div className="p-8 text-center text-xs text-slate-400">Loading client records...</div>
            ) : filteredClients.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                <Users className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <p className="font-semibold text-slate-300">No client records found.</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {statusFilter === 'INACTIVE'
                    ? 'No inactive clients on file.'
                    : statusFilter === 'PENDING_INTAKE'
                    ? 'No clients currently pending intake.'
                    : 'Intake a client using the button above to begin the care lifecycle.'}
                </p>
              </div>
            ) : (
              <div className={`divide-y max-h-[700px] overflow-y-auto ${isClinicalStaff ? 'divide-slate-100' : 'divide-slate-800/90 md:divide-slate-800/50'}`}>
                {filteredClients.map((client, idx) => {
                  const isSelected = selectedClientId === client.id;
                  const isInactive = (client.status || 'ACTIVE').toUpperCase() === 'INACTIVE';
                  return (
                    <div
                      key={client.id}
                      onClick={() => {
                        setLastSelectedMode('roster');
                        onSelectClient(client.id);
                      }}
                      className={`group relative overflow-hidden px-3 py-2 sm:px-3.5 sm:py-2.5 cursor-pointer transition-colors duration-150 border-l-[3px] focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-400/60 select-none ${
                        isSelected
                          ? isClinicalStaff
                            ? 'bg-slate-100/90 border-l-slate-800 text-slate-950 font-semibold shadow-none'
                            : 'ops-row-active border-l-cyan-400 shadow-[inset_0_1px_0_0_rgba(103,232,249,0.18)]'
                          : isClinicalStaff
                          ? `${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} border-l-transparent hover:bg-slate-100/60 text-slate-900`
                          : isInactive
                          ? `opacity-70 ${idx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'} border-l-transparent hover:opacity-95 hover:border-l-slate-500 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]`
                          : `${idx % 2 === 0 ? 'ops-row-a' : 'ops-row-b'} border-l-transparent hover:border-l-cyan-400/80 hover:bg-[#20314a] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]`
                      }`}
                    >
                      {/* Subtle chrome/silver sheen across the row on hover */}
                      {!isClinicalStaff && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
                      )}
                      {selectedClientId ? (
                        /* Compact 2-line mode for sidebar view when client chart is open */
                        <div className="space-y-0.5 min-w-0">
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className={`font-semibold text-xs truncate min-w-0 flex-1 ${
                              isClinicalStaff
                                ? isSelected ? 'text-slate-950 font-bold' : 'text-slate-900 group-hover:text-slate-950'
                                : isSelected ? 'text-cyan-200 font-bold' : 'text-slate-100 group-hover:text-cyan-300'
                            }`}>
                              {client.last_name}, {client.first_name}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded border bg-sky-50 text-sky-800 border-sky-200/80">
                                {client.active_auths_count || 0} auth
                              </span>
                              {isInactive ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                    INACTIVE
                                  </span>
                                  {canChangeClientStatus && (
                                    <button
                                      type="button"
                                      disabled={statusUpdating}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Reactivate client ${client.first_name} ${client.last_name}?`)) {
                                          handleToggleClientStatus('ACTIVE', client.id);
                                        }
                                      }}
                                      className="text-[9px] font-medium px-1 py-0.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 border border-emerald-500/30 transition-colors"
                                      title={`Reactivate ${client.first_name} ${client.last_name}`}
                                    >
                                      Reactivate
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                                  {client.status || 'ACTIVE'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={`flex items-center justify-between gap-2 text-[10px] ${isClinicalStaff ? 'text-slate-500' : 'text-slate-400'}`}>
                            <div className="font-mono truncate min-w-0">
                              {!isClinicalStaff ? (
                                <>
                                  <span className="text-slate-500">#</span>{client.id} <span className="text-slate-600 mx-1">•</span> DOB: {client.dob || '—'}
                                </>
                              ) : (
                                <>DOB: {client.dob || '—'}</>
                              )}
                            </div>
                            {client.primary_payer_name && (
                              <span
                                className="px-1.5 py-0.5 rounded truncate max-w-[120px] font-medium shrink-0 bg-slate-100 text-slate-700 border border-slate-200/90"
                                title={client.primary_payer_name}
                              >
                                {client.primary_payer_name}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* MOBILE VIEW: Compact Name-Only Client Row */}
                          <div className="block sm:hidden py-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`font-semibold text-xs sm:text-[13px] truncate ${isSelected ? 'text-slate-950 font-bold' : 'text-slate-800 group-hover:text-slate-950'}`}>
                                {client.last_name}, {client.first_name}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 group-hover:text-slate-700 transition-colors" />
                            </div>
                          </div>

                          {/* DESKTOP / TABLET VIEW: Sleek single-line directory row */}
                          <div className="hidden sm:flex items-center justify-between gap-3 md:gap-4 min-w-0">
                            {/* Left: Client Name (ALWAYS visible, flex-1) + ID / DOB (if space permits) */}
                            <div className="flex items-center gap-2 md:gap-2.5 min-w-0 flex-1">
                              {isSelected && !isClinicalStaff && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 shadow-[0_0_6px_rgba(34,211,238,0.8)]" />}
                              <span className={`font-semibold text-xs sm:text-[13px] truncate min-w-0 ${
                                isClinicalStaff
                                  ? isSelected ? 'text-slate-950 font-bold' : 'text-slate-900 group-hover:text-slate-950'
                                  : isSelected ? 'text-cyan-200 font-bold' : 'text-slate-100 group-hover:text-cyan-300'
                              }`}>
                                {client.last_name}, {client.first_name}
                              </span>
                              <span className={`text-[11px] font-mono shrink-0 flex items-center gap-1.5 ${isClinicalStaff ? 'text-slate-500' : 'text-slate-400'}`}>
                                {!isClinicalStaff ? (
                                  <>
                                    <span><span className="text-slate-500">#</span><span className={isSelected ? 'text-cyan-300 font-semibold' : 'text-slate-300'}>{client.id}</span></span>
                                    <span className="hidden md:inline text-slate-600">•</span>
                                    <span className="hidden md:inline text-slate-400">DOB: {client.dob || '—'}</span>
                                  </>
                                ) : (
                                  <span>DOB: {client.dob || '—'}</span>
                                )}
                              </span>
                            </div>

                            {/* Right: Payer / Program tag (lg+) + Auth count (xl+) + Status pill (always) */}
                            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                              {client.primary_payer_name && (
                                <span
                                  className="hidden lg:inline-block text-[10px] font-medium px-2 py-0.5 rounded max-w-[160px] xl:max-w-[200px] truncate shrink-0 bg-slate-100 text-slate-700 border border-slate-200/90"
                                  title={client.primary_payer_name}
                                >
                                  {client.primary_payer_name}
                                </span>
                              )}

                              <span className="hidden xl:inline-flex items-center text-[10px] font-mono font-medium px-2 py-0.5 rounded shrink-0 bg-sky-50 text-sky-800 border border-sky-200/80">
                                {client.active_auths_count || 0} active auth{(client.active_auths_count || 0) === 1 ? '' : 's'}
                              </span>

                              {isInactive ? (
                                <div className="flex items-center space-x-1 shrink-0">
                                  <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                    INACTIVE
                                  </span>
                                  {canChangeClientStatus && (
                                    <button
                                      type="button"
                                      disabled={statusUpdating}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Reactivate client ${client.first_name} ${client.last_name}?`)) {
                                          handleToggleClientStatus('ACTIVE', client.id);
                                        }
                                      }}
                                      className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 border border-emerald-500/30 transition-colors inline-flex items-center space-x-1 shrink-0"
                                      title={`Reactivate ${client.first_name} ${client.last_name}`}
                                    >
                                      <UserCheck className="w-2.5 h-2.5" />
                                      <span>Reactivate</span>
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200/80">
                                  {client.status || 'ACTIVE'}
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Client Chart Detail View Column */}
        {selectedClientId && clientDetail && (
          <div className="w-full lg:col-span-8 space-y-4 relative z-0">
            {/* Mobile Back Button: List → Detail → Back */}
            <div className="lg:hidden">
              <button
                type="button"
                onClick={() => {
                  onSelectClient(null);
                  if (lastSelectedMode === 'expirations') {
                    setViewMode('expirations');
                  }
                }}
                className="w-full flex items-center px-4 py-2.5 rounded-xl bg-white border border-slate-200/90 text-slate-700 hover:text-slate-900 font-medium text-xs shadow-xs transition-all active:scale-[0.99] cursor-pointer"
              >
                <div className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4 text-slate-600" />
                  <span>
                    Back to {lastSelectedMode === 'expirations' ? 'POC & Auth Expirations' : 'Client Directory'}
                  </span>
                </div>
              </button>
            </div>

            <div className="relative rounded-2xl overflow-hidden bg-slate-50/60 border border-slate-200/90 shadow-xs">
              {/* Chart Header */}
              <div className="p-4 sm:p-5 border-b border-slate-800/80 bg-gradient-to-r from-[#18263c] via-[#142034] to-[#0f1726] text-white flex flex-col gap-3 sm:gap-3.5">
                {/* MOBILE ROW 1 (Phone < sm): Centered Client Identity + Close X */}
                <div className="relative flex items-center justify-center min-h-[32px] w-full sm:hidden px-8">
                  <div className="flex items-center justify-center min-w-0 max-w-full">
                    <h2 className="text-sm font-bold text-white tracking-tight truncate">
                      {clientDetail.client.last_name}, {clientDetail.client.first_name}
                    </h2>
                  </div>
                  <button
                    onClick={() => onSelectClient(null)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 inline-flex items-center justify-center shrink-0 transition-colors border border-transparent hover:border-slate-700/60"
                    title="Close Chart"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* MOBILE ROW 2 (Phone < sm): Action Rail ([ AI Assist ] [ Mark Inactive / Reactivate ]) */}
                <div className="flex sm:hidden items-center gap-2 w-full">
                  <button
                    onClick={() =>
                      onOpenAIWithContext(
                        `Summarize client history, active authorizations, goals, and pending documentation for ${clientDetail.client.first_name} ${clientDetail.client.last_name}.`,
                        clientDetail.client.id
                      )
                    }
                    className="flex-1 h-9 px-3 rounded-xl bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-100 text-xs font-semibold inline-flex items-center justify-center space-x-1.5 transition-all shrink-0 whitespace-nowrap cursor-pointer active:scale-[0.98]"
                    title="AI Assist client overview"
                  >
                    <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4.5 h-4.5 shrink-0 object-contain" />
                    <span className="text-slate-100 font-semibold text-xs tracking-tight">AI Assist</span>
                  </button>

                  {canChangeClientStatus && (
                    clientDetail.client.status === 'INACTIVE' ? (
                      <button
                        type="button"
                        disabled={statusUpdating}
                        onClick={() => handleToggleClientStatus('ACTIVE')}
                        className="flex-1 h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold inline-flex items-center justify-center space-x-1.5 transition-all shadow-xs active:scale-[0.98] cursor-pointer disabled:opacity-50"
                        title="Reactivate Client"
                      >
                        <UserCheck className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{statusUpdating ? 'Updating...' : 'Reactivate'}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={statusUpdating}
                        onClick={() => setShowInactiveConfirmModal(true)}
                        className="flex-1 h-9 px-3 rounded-xl bg-rose-950/40 hover:bg-rose-900/50 text-rose-200 border border-rose-800/60 text-xs font-semibold inline-flex items-center justify-center space-x-1.5 transition-all shadow-xs active:scale-[0.98] cursor-pointer disabled:opacity-50"
                        title="Mark client Inactive"
                      >
                        <UserX className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                        <span className="truncate">{statusUpdating ? 'Updating...' : 'Mark Inactive'}</span>
                      </button>
                    )
                  )}
                </div>

                {/* MOBILE ROW 3 (Phone < sm): Full-width Universal Tools for Administrative Roles */}
                {isAdministrativeRole && (
                  <div className="flex sm:hidden w-full pt-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (onOpenUniversalTools) {
                          onOpenUniversalTools();
                        } else {
                          setShowCallModal(true);
                        }
                      }}
                      className="w-full h-9 px-3.5 rounded-xl bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-100 text-xs font-semibold inline-flex items-center justify-center space-x-2 transition-all shadow-xs active:scale-[0.98]"
                      title="Open Universal Tools for this client"
                    >
                      <Wrench className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      <span className="truncate">Universal Tools</span>
                    </button>
                  </div>
                )}

                {/* DESKTOP / TABLET ROW 1 (>= sm): Full Client Name (Left) + Close X (Right) */}
                <div className="hidden sm:flex items-center justify-between gap-4 w-full">
                  <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight min-w-0">
                    {clientDetail.client.last_name}, {clientDetail.client.first_name}
                  </h2>
                  <button
                    onClick={() => onSelectClient(null)}
                    className="h-8 w-8 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 inline-flex items-center justify-center shrink-0 transition-colors border border-transparent hover:border-slate-700/60"
                    title="Close Chart"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* DESKTOP / TABLET ROW 2 (>= sm): Status (Left) + Actions (Right) */}
                <div className="hidden sm:flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 w-full">
                  <div className="flex items-center space-x-2.5 shrink-0">
                    <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-200 border border-slate-700 font-semibold shadow-xs">
                      ID: {clientDetail.client.id}
                    </span>
                    <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase shadow-xs ${
                      clientDetail.client.status === 'INACTIVE'
                        ? 'bg-slate-800 text-slate-400 border border-slate-700'
                        : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                    }`}>
                      {clientDetail.client.status || 'ACTIVE'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {canOperateClients && (
                      <button
                        onClick={() => {
                          setEditModalSection('general');
                          setShowEditModal(true);
                        }}
                        className="h-8 px-3 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-100 border border-slate-700/80 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shadow-xs shrink-0 whitespace-nowrap active:scale-[0.98]"
                        title="Edit client demographics, guardian, physician, and POC dates"
                      >
                        <Edit3 className="w-3.5 h-3.5 text-slate-300" />
                        <span>Edit</span>
                      </button>
                    )}

                    {canChangeClientStatus && (
                      clientDetail.client.status === 'INACTIVE' ? (
                        <button
                          type="button"
                          disabled={statusUpdating}
                          onClick={() => handleToggleClientStatus('ACTIVE')}
                          className="h-8 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shrink-0 whitespace-nowrap shadow-xs"
                          title="Reactivate this client"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          <span>{statusUpdating ? 'Updating...' : 'Reactivate'}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={statusUpdating}
                          onClick={() => setShowInactiveConfirmModal(true)}
                          className="h-8 px-2.5 rounded-lg bg-rose-950/40 hover:bg-rose-900/50 text-rose-200 border border-rose-800/60 text-xs font-medium inline-flex items-center space-x-1.5 transition-all shrink-0 whitespace-nowrap shadow-xs cursor-pointer"
                          title="Mark client Inactive (preserves all data)"
                        >
                          <UserX className="w-3.5 h-3.5 text-rose-400" />
                          <span>{statusUpdating ? 'Updating...' : 'Mark Inactive'}</span>
                        </button>
                      )
                    )}

                    {canOperateClients && (
                      <button
                        onClick={() => setShowCallModal(true)}
                        className="h-8 px-3.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-100 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shrink-0 whitespace-nowrap shadow-xs"
                        title="Launch phone call to client or guardian"
                      >
                        <Phone className="w-3.5 h-3.5 text-slate-300" />
                        <span>Call</span>
                      </button>
                    )}

                    {canOperateClients && (
                      <button
                        onClick={() => setShowFaxModal(true)}
                        className="h-8 px-3.5 rounded-lg bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-100 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shrink-0 whitespace-nowrap shadow-xs"
                        title="Transmit electronic fax for this client"
                      >
                        <Printer className="w-3.5 h-3.5 text-slate-300" />
                        <span>Fax</span>
                      </button>
                    )}

                    <button
                      onClick={() =>
                        onOpenAIWithContext(
                          `Summarize client history, active authorizations, goals, and pending documentation for ${clientDetail.client.first_name} ${clientDetail.client.last_name}.`,
                          clientDetail.client.id
                        )
                      }
                      className="h-8 px-3 rounded-lg bg-slate-800/90 hover:bg-slate-700 border border-slate-700/80 text-slate-100 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shrink-0 whitespace-nowrap active:scale-[0.98]"
                      title="AI Assist client overview"
                    >
                      <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4.5 h-4.5 shrink-0 object-contain" />
                      <span className="text-slate-100 font-semibold text-xs tracking-tight">AI Assist</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Inactive Client Notice Banner */}
              {clientDetail.client.status === 'INACTIVE' && (
                <div className="mx-4 sm:mx-6 mt-4 p-3.5 bg-amber-50/90 border border-amber-200/90 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-xs text-amber-900">
                  <div className="flex items-center space-x-2.5 text-amber-800">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <div>
                      <span className="font-bold text-amber-950">Client Inactive:</span> All authorizations, goals, clinical documentation, SOAPs, billing history, and contacts are preserved.
                    </div>
                  </div>
                  {canChangeClientStatus && (
                    <button
                      type="button"
                      disabled={statusUpdating}
                      onClick={() => handleToggleClientStatus('ACTIVE')}
                      className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition-all whitespace-nowrap shadow-xs"
                    >
                      {statusUpdating ? 'Updating...' : 'Reactivate Client'}
                    </button>
                  )}
                </div>
              )}

              {/* Primary Client-Chart Navigation (Audited Specification) */}
              {/* Mobile (<sm): wrap authorized tabs into two compact rows without horizontal scrolling */}
              {/* Desktop/Tablet (>=sm): single-row horizontal bar with role permissions unchanged */}
              {(() => {
                const authorizedTabs = (['Overview', 'Auths', 'Goals', 'Visits', 'Clinical Docs', 'Billing', 'Notes'] as const)
                  .filter(tab => !(tab === 'Billing' && isClinicalStaff));
                const splitIndex = authorizedTabs.length === 7 ? 4 : Math.ceil(authorizedTabs.length / 2);
                const mobileRow1 = authorizedTabs.slice(0, splitIndex);
                const mobileRow2 = authorizedTabs.slice(splitIndex);

                return (
                  <>
                    {/* Phone Widths (< sm): Single compact row with More/Back toggle, zero horizontal scroll */}
                    <div className="sm:hidden border-b border-slate-200/90 bg-slate-100/70 p-1.5">
                      {!showSecondaryMobileNav ? (
                        /* DEFAULT ROW: [ Overview ] [ Auths ] [ Goals ] [ More ] */
                        <div className="flex items-center gap-1">
                          {(['Overview', 'Auths', 'Goals'] as const).map((tab) => {
                            const isActive = chartTab === tab;
                            return (
                              <button
                                key={tab}
                                type="button"
                                onClick={() => setChartTab(tab)}
                                className={`flex-1 min-w-0 py-2 px-1 text-center text-[11px] truncate rounded-lg transition-all border ${
                                  isActive
                                    ? 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                                    : 'bg-slate-50/80 text-slate-600 hover:text-slate-900 border-slate-200/80 font-medium'
                                }`}
                              >
                                {tab}
                              </button>
                            );
                          })}

                          {/* More Toggle Button */}
                          <button
                            type="button"
                            onClick={() => setShowSecondaryMobileNav(true)}
                            className={`flex-1 min-w-0 py-2 px-1 text-center text-[11px] truncate rounded-lg transition-all border flex items-center justify-center gap-0.5 ${
                              isSecondaryChartTab(chartTab)
                                ? 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                                : 'bg-slate-50/80 text-slate-600 hover:text-slate-900 border-slate-200/80 font-medium'
                            }`}
                            title="More chart sections"
                            id="mobile-client-nav-more-btn"
                          >
                            <span>More</span>
                            <ChevronRight className="w-3 h-3 text-slate-500 shrink-0" />
                          </button>
                        </div>
                      ) : (
                        /* SECONDARY ROW: [ ‹ ] [ Visits ] [ Clinical Docs ] [ Billing ] [ Notes ] */
                        <div className="flex items-center gap-1">
                          {/* Back to Primary Navigation Control */}
                          <button
                            type="button"
                            onClick={() => setShowSecondaryMobileNav(false)}
                            className="py-2 px-2 text-center text-[11px] font-semibold rounded-lg bg-slate-50/80 text-slate-600 hover:text-slate-900 border border-slate-200/80 flex items-center justify-center shrink-0 transition-all active:scale-95"
                            title="Back to Primary Sections"
                            aria-label="Back"
                            id="mobile-client-nav-back-btn"
                          >
                            <ChevronLeft className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span className="sr-only">Back</span>
                          </button>

                          {/* Secondary authorized tabs */}
                          {(['Visits', 'Clinical Docs', 'Billing', 'Notes'] as const)
                            .filter(tab => !(tab === 'Billing' && isClinicalStaff))
                            .map((tab) => {
                              const isActive = chartTab === tab;
                              return (
                                <button
                                  key={tab}
                                  type="button"
                                  onClick={() => setChartTab(tab)}
                                  className={`flex-1 min-w-0 py-2 px-1 text-center text-[11px] truncate rounded-lg transition-all border ${
                                    isActive
                                      ? 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                                      : 'bg-slate-50/80 text-slate-600 hover:text-slate-900 border-slate-200/80 font-medium'
                                  }`}
                                >
                                  {tab}
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    {/* Tablet / Desktop (>= sm): Preserved single-row layout */}
                    <div className="hidden sm:flex border-b border-slate-200/90 bg-slate-100/70 px-4 py-2 overflow-x-auto gap-1.5">
                      {authorizedTabs.map((tab) => {
                        const isActive = chartTab === tab;
                        return (
                          <button
                            key={tab}
                            type="button"
                            onClick={() => setChartTab(tab)}
                            className={`px-3.5 py-2 text-xs whitespace-nowrap rounded-xl transition-all border ${
                              isActive
                                ? 'bg-white text-slate-900 border-slate-300 font-bold shadow-xs'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 border-transparent font-medium'
                            }`}
                          >
                            {tab}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              {/* Chart Content Area */}
              <div className="p-3 sm:p-6">
                {/* 1. OVERVIEW TAB */}
                {chartTab === 'Overview' && (
                  <div className="space-y-4">
                    {/* 1. PRIMARY TOP OVERVIEW SECTION: CLIENT PROFILE */}
                    <div className="rounded-2xl p-5 space-y-4 bg-white border border-slate-200/90 shadow-xs">
                      {/* Mobile Header (< sm): Centered Section Identity + Status Pill, Secondary Edit Action Underneath */}
                      <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1.5 border-b border-slate-200/80 pb-2.5">
                        <div className="flex items-center justify-center gap-2">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">Client Profile</h3>
                          <span className={`text-[9.5px] font-mono px-2 py-0.5 rounded-full font-bold uppercase shadow-2xs shrink-0 ${
                            clientDetail.client.status === 'INACTIVE'
                              ? 'bg-slate-100 text-slate-600 border border-slate-200'
                              : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          }`}>
                            {clientDetail.client.status || 'ACTIVE'}
                          </span>
                        </div>
                        {canOperateClients && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditModalSection('general');
                              setShowEditModal(true);
                            }}
                            className="h-6 px-2.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-[10.5px] font-medium inline-flex items-center space-x-1 transition-all shadow-2xs active:scale-[0.98]"
                            title="Edit demographics, identifiers, addresses, contacts, guardian, and POC"
                          >
                            <Edit3 className="w-3 h-3 text-slate-500 shrink-0" />
                            <span>Edit Chart</span>
                          </button>
                        )}
                      </div>

                      {/* Desktop Header (>= sm): One clean row, title + status on left, Edit Chart on right */}
                      <div className="hidden sm:flex items-center justify-between gap-3 border-b border-slate-200/80 pb-3">
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <div className="shrink-0">
                            <IconBadge icon={Shield} variant="slate" size="md" />
                          </div>
                          <div className="flex items-center space-x-2 min-w-0">
                            <h3 className="text-sm font-bold uppercase tracking-wider truncate text-slate-900">Client Profile</h3>
                            <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase shadow-2xs shrink-0 ${
                              clientDetail.client.status === 'INACTIVE'
                                ? 'bg-slate-100 text-slate-600 border border-slate-200'
                                : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            }`}>
                              {clientDetail.client.status || 'ACTIVE'}
                            </span>
                          </div>
                        </div>

                        {canOperateClients && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditModalSection('general');
                              setShowEditModal(true);
                            }}
                            className="h-8 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shadow-2xs active:scale-[0.98] shrink-0"
                            title="Edit demographics, identifiers, addresses, contacts, guardian, and POC"
                          >
                            <Edit3 className="w-3.5 h-3.5 shrink-0 text-slate-500" />
                            <span>Edit Chart</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                        {/* Column 1: Identifiers & Demographics */}
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-center md:justify-start space-x-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                              Identifiers & Demographics
                            </h4>
                          </div>
                          <div className="rounded-xl p-4 bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <div className="grid grid-cols-2 gap-3.5">
                              <div>
                                <span className="block text-[11px] text-slate-500">AHCCCS Medicaid ID</span>
                                <span className="font-mono font-bold text-xs text-slate-900">{clientDetail.client.ahcccs_id || 'Not on file'}</span>
                              </div>
                              <div>
                                <span className="block text-[11px] text-slate-500">WellSky Case #</span>
                                <span className="font-mono text-xs text-slate-800">{clientDetail.client.wellsky_caseno || 'Not on file'}</span>
                              </div>
                              <div>
                                <span className="block text-[11px] text-slate-500">DDD District</span>
                                <span className="font-mono font-bold text-xs text-slate-900">{clientDetail.client.district_code || 'Unassigned'}</span>
                              </div>
                              <div>
                                <span className="block text-[11px] text-slate-500">Client ID</span>
                                <span className="font-mono text-xs text-slate-700">{clientDetail.client.id}</span>
                              </div>
                              {clientDetail.client.wellsky_client_id && clientDetail.client.wellsky_client_id.trim() !== '' ? (
                                <div>
                                  <span className="block text-[11px] text-slate-500">WellSky Client ID</span>
                                  <span className="font-mono text-xs text-slate-700">{clientDetail.client.wellsky_client_id}</span>
                                </div>
                              ) : null}
                              <div>
                                <span className="block text-[11px] text-slate-500">Date of Birth</span>
                                <span className="font-medium text-xs text-slate-800">{clientDetail.client.dob}</span>
                              </div>
                              <div>
                                <span className="block text-[11px] text-slate-500">Gender</span>
                                <span className="text-xs text-slate-800">{clientDetail.client.gender || 'Not specified'}</span>
                              </div>
                              <div className="col-span-2 pt-2 border-t border-slate-200/80 flex items-center justify-between">
                                <span className="text-[11px] text-slate-500">Primary Language:</span>
                                <span className="font-medium text-xs text-slate-800">{clientDetail.client.primary_language || 'English'}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Column 2: Service Address & Contacts */}
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-center md:justify-start space-x-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                              Service Address & Contacts
                            </h4>
                          </div>
                          <div className="rounded-xl p-4 space-y-3 bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            {(() => {
                              const savedAddresses = clientDetail.addresses || [];
                              const treatmentRow = savedAddresses.find(isTreatmentAddress);
                              const treatmentText =
                                (treatmentRow?.formatted || '').trim() ||
                                clientDetail.client.service_address ||
                                '';
                              const additionalAddresses = savedAddresses.filter((addr) => {
                                if (treatmentRow && addr.id === treatmentRow.id) return false;
                                return true;
                              });
                              return (
                                <>
                                  <div>
                                    <span className="block text-[11px] text-slate-500">Service / Treatment Address</span>
                                    <span className="font-medium text-xs flex items-start space-x-1.5 mt-1 text-slate-800">
                                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" />
                                      <span>{treatmentText || 'No service address on file'}</span>
                                    </span>
                                    {treatmentRow?.label ? (
                                      <span className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        {treatmentRow.label}
                                      </span>
                                    ) : null}
                                  </div>
                                  {(() => {
                                    const memberAddress = formatSavedMemberAddress({
                                      street: clientDetail.client.address_line1,
                                      city: clientDetail.client.city,
                                      state: clientDetail.client.state,
                                      zip: clientDetail.client.zip
                                    });
                                    if (!memberAddress) return null;
                                    return (
                                      <div>
                                        <span className="block text-[11px] text-slate-500">Member address</span>
                                        <span className="text-xs text-slate-800">{memberAddress}</span>
                                      </div>
                                    );
                                  })()}

                                  {additionalAddresses.length > 0 && (
                                    <div className="space-y-1.5 pt-1">
                                      <span className="block text-[11px] text-slate-500">Saved addresses</span>
                                      {additionalAddresses.map((addr) => (
                                        <div
                                          key={addr.id}
                                          className="flex items-start justify-between gap-2 rounded-lg border border-slate-200/80 bg-white px-2.5 py-1.5"
                                        >
                                          <div className="min-w-0">
                                            <span className="block text-[11px] font-semibold text-slate-700">
                                              {addr.label || addr.role}
                                            </span>
                                            <span className="block text-[11px] text-slate-600 leading-snug">
                                              {addr.formatted || '—'}
                                            </span>
                                          </div>
                                          {canOperateClients && (
                                            <div className="flex shrink-0 flex-col items-end gap-0.5">
                                              <button
                                                type="button"
                                                onClick={() => handleSetTreatmentAddress(addr.id)}
                                                disabled={settingTreatmentId === addr.id}
                                                className="text-[10px] font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-50"
                                              >
                                                {settingTreatmentId === addr.id ? 'Saving…' : 'Set as Treatment Address'}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleArchiveClientAddress(addr.id)}
                                                disabled={archivingAddressId === addr.id}
                                                className="text-[10px] font-medium text-slate-500 hover:text-rose-700 disabled:opacity-50"
                                              >
                                                {archivingAddressId === addr.id ? 'Deleting…' : 'Delete'}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {addressActionError && (
                                    <p className="text-[11px] text-rose-600">{addressActionError}</p>
                                  )}

                                  {canOperateClients && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAddressActionError(null);
                                        setAddressForm({ label: 'Home', role: 'HOME', formatted: '' });
                                        setShowAddAddressModal(true);
                                      }}
                                      className="inline-flex items-center space-x-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
                                    >
                                      <Plus className="w-3 h-3" />
                                      <span>Add Address</span>
                                    </button>
                                  )}
                                </>
                              );
                            })()}

                            {clientDetail.client.home_address && (
                              <div>
                                <span className="block text-[11px] text-slate-500">Home Address</span>
                                <span className="text-xs text-slate-700">{clientDetail.client.home_address}</span>
                              </div>
                            )}

                            {(() => {
                              const savedPhones = clientDetail.phones || [];
                              const primaryRow = savedPhones.find(isPrimaryPhone);
                              const primaryText =
                                (primaryRow?.number || '').trim() ||
                                clientDetail.client.phone ||
                                '';
                              const additionalPhones = savedPhones.filter((phone) => {
                                if (primaryRow && phone.id === primaryRow.id) return false;
                                return true;
                              });
                              return (
                                <div className="pt-2 border-t border-slate-200/80 space-y-2">
                                  <div>
                                    <span className="block text-[11px] text-slate-500">Primary Phone</span>
                                    <span className="text-xs flex items-center space-x-1 mt-0.5 text-slate-800">
                                      <Phone className="w-3 h-3 shrink-0 text-slate-500" />
                                      <span>{primaryText || 'No phone'}</span>
                                    </span>
                                    {primaryRow?.label ? (
                                      <span className="mt-0.5 inline-block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        {primaryRow.label}
                                      </span>
                                    ) : null}
                                  </div>
                                  {additionalPhones.length > 0 && (
                                    <div className="space-y-1">
                                      {additionalPhones.map((phone) => (
                                        <div key={phone.id} className="flex items-start justify-between gap-2">
                                          <div className="min-w-0">
                                            <span className="block text-[11px] font-semibold text-slate-700">
                                              {phone.label || phone.kind}
                                            </span>
                                            <span className="block text-[11px] text-slate-600">{phone.number}</span>
                                          </div>
                                          <div className="flex shrink-0 flex-col items-end gap-0.5">
                                            <button
                                              type="button"
                                              onClick={() => handleSetPrimaryPhone(phone.id)}
                                              disabled={settingPrimaryPhoneId === phone.id}
                                              className="text-[10px] font-semibold text-slate-700 hover:text-slate-900 disabled:opacity-50"
                                            >
                                              {settingPrimaryPhoneId === phone.id ? 'Saving…' : 'Set as Primary Phone'}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleArchiveClientPhone(phone.id)}
                                              disabled={archivingPhoneId === phone.id}
                                              className="text-[10px] font-medium text-slate-500 hover:text-rose-700 disabled:opacity-50"
                                            >
                                              {archivingPhoneId === phone.id ? 'Deleting…' : 'Delete'}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {phoneActionError && (
                                    <p className="text-[11px] text-rose-600">{phoneActionError}</p>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPhoneActionError(null);
                                      setPhoneForm({ label: 'Client', number: '' });
                                      setShowAddPhoneModal(true);
                                    }}
                                    className="inline-flex items-center space-x-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
                                  >
                                    <Plus className="w-3 h-3" />
                                    <span>Add Phone</span>
                                  </button>
                                </div>
                              );
                            })()}

                            <div className="pt-2 border-t border-slate-200/80">
                              <span className="block text-[11px] text-slate-500">Email</span>
                              <span className="text-xs flex items-center space-x-1 mt-0.5 truncate text-slate-800" title={clientDetail.client.email || ''}>
                                <Mail className="w-3 h-3 shrink-0 text-slate-500" />
                                <span className="truncate">{clientDetail.client.email || 'No email'}</span>
                              </span>
                            </div>

                            {clientDetail.client.emergency_contact_name && (
                              <div className="pt-2 border-t border-slate-200/80 text-[11px]">
                                <span className="block text-slate-500">Emergency Contact</span>
                                <span className="font-medium text-slate-800">
                                  {clientDetail.client.emergency_contact_name}
                                  {clientDetail.client.emergency_contact_relationship && ` (${clientDetail.client.emergency_contact_relationship})`}
                                  {clientDetail.client.emergency_contact_phone && ` • ${clientDetail.client.emergency_contact_phone}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Primary & Commercial / TPL Insurance Coverage */}
                    <div className="rounded-2xl p-5 space-y-3.5 bg-white border border-slate-200/90 shadow-xs">
                      {/* Mobile Header (< sm) */}
                      <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1.5 border-b border-slate-200/80 pb-2.5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                          Insurance & Third-Party Liability (TPL)
                        </h4>
                        <span className="text-[9.5px] font-mono px-2 py-0.5 rounded-full font-bold uppercase shadow-2xs bg-slate-100 text-slate-700 border border-slate-200">
                          {clientDetail.client.secondary_payer_name ? 'Primary Medicaid + Commercial TPL' : 'Medicaid / DDD Primary'}
                        </span>
                      </div>

                      {/* Desktop Header (>= sm): Clean Title + Status Badge */}
                      <div className="hidden sm:flex items-center justify-between gap-3 border-b border-slate-200/80 pb-2.5">
                        <div className="flex items-center space-x-2 min-w-0">
                          <div className="shrink-0">
                            <IconBadge icon={Shield} variant="slate" size="sm" />
                          </div>
                          <h4 className="text-xs font-bold uppercase tracking-wider truncate text-slate-900">
                            Insurance & Third-Party Liability (TPL)
                          </h4>
                        </div>
                        <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase shadow-2xs shrink-0 bg-slate-100 text-slate-700 border border-slate-200">
                          {clientDetail.client.secondary_payer_name ? 'Primary Medicaid + Commercial TPL' : 'Medicaid / DDD Primary'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        {/* Primary Coverage: DDD / AHCCCS */}
                        <div className="p-3.5 rounded-xl space-y-2 bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase font-bold text-slate-700">Primary Payer (Medicaid)</span>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full font-bold shadow-2xs bg-emerald-50 text-emerald-800 border border-emerald-200">
                              ACTIVE
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">DES/DDD — Arizona Department of Economic Security</div>
                            <div className="text-[11px] font-mono mt-1 space-y-0.5 text-slate-600">
                              <div>AHCCCS ID: <span className="font-semibold text-slate-900">{clientDetail.client.ahcccs_id || 'Not on file'}</span></div>
                              <div>WellSky Case #: <span className="text-slate-700">{clientDetail.client.wellsky_caseno || 'Not on file'}</span></div>
                              <div>District: <span className="font-bold text-slate-900">{clientDetail.client.district_code || 'Unassigned'}</span></div>
                            </div>
                          </div>
                        </div>

                        {/* Secondary / Commercial TPL Coverage */}
                        <div className="p-3.5 rounded-xl space-y-2 bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase font-bold text-slate-700">Secondary / Commercial TPL Insurance</span>
                            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-bold uppercase shadow-2xs ${
                              clientDetail.client.secondary_payer_name
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}>
                              {clientDetail.client.secondary_payer_name ? (clientDetail.client.tpl_status || 'VERIFIED') : 'NONE'}
                            </span>
                          </div>

                          {clientDetail.client.secondary_payer_name ? (
                            <div className="space-y-1 text-[11px]">
                              <div className="font-semibold text-slate-900">{clientDetail.client.secondary_payer_name}</div>
                              <div className="grid grid-cols-2 gap-2 font-mono mt-1 pt-1.5 border-t border-slate-200/80 text-slate-600">
                                <div>Member ID: <span className="font-semibold text-slate-800">{clientDetail.client.secondary_policy_id || 'N/A'}</span></div>
                                <div>Group #: <span className="font-semibold text-slate-800">{clientDetail.client.secondary_group_id || 'N/A'}</span></div>
                                <div>Subscriber: <span className="font-semibold text-slate-800">{clientDetail.client.insured_name || 'Self'}</span></div>
                                <div>Relationship: <span className="font-semibold text-slate-800">{clientDetail.client.subscriber_relationship || 'Child'}</span></div>
                                {clientDetail.client.insured_dob && (
                                  <div>Sub. DOB: <span className="font-semibold text-slate-800">{clientDetail.client.insured_dob}</span></div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-[11px] py-1 text-slate-500">
                              No commercial or third-party liability insurance on file. DDD / AHCCCS Medicaid is billed as primary.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 2. INTAKE PACKET & SERVICE AGREEMENT LIFECYCLE */}
                    {(() => {
                      const intakeDoc = clientDetail.documents?.find(
                        (d: any) => d.title?.includes('Client Intake Packet') || d.id === clientDetail.client.intake_packet_doc_id
                      );
                      const rawStatus = clientDetail.client.intake_status || intakeDoc?.intake_status || 'Ready to Send';
                      const isFailedDispatch = rawStatus === 'Failed Dispatch' || (intakeDoc?.esign_status === 'FAILED' && !intakeDoc?.esign_envelope_id) || (rawStatus.includes('Sent') && !intakeDoc?.esign_envelope_id);
                      const currentStatus = isFailedDispatch ? 'Failed Dispatch' : rawStatus;
                      const guardianSigned = Boolean(intakeDoc?.guardian_signature || intakeDoc?.guardian_signed_at);
                      const therapistSigned = Boolean(intakeDoc?.therapist_signature || intakeDoc?.therapist_signed_at);
                      const canComplete = (guardianSigned && therapistSigned) || currentStatus === 'Fully Signed';
                      const isComplete = currentStatus === 'Complete';
                      const isTherapistAssigned = Boolean(clientDetail.client.assigned_provider_id);
                      const hasGuardianEmail = Boolean(clientDetail.client.guardian_email?.trim() || clientDetail.guardian?.email?.trim());
                      const missingPacketPrereqs: string[] = [];
                      if (!isTherapistAssigned) {
                        missingPacketPrereqs.push('Assign a treating therapist');
                      }
                      if (!hasGuardianEmail) {
                        missingPacketPrereqs.push('Provide guardian email');
                      }
                      const packetDispatchDisabledReason = missingPacketPrereqs.length === 0
                        ? null
                        : missingPacketPrereqs.length === 1
                        ? `${missingPacketPrereqs[0]} before sending the intake packet for eSignature.`
                        : `${missingPacketPrereqs.join(' and ')} before sending the intake packet for eSignature.`;
                      const isActiveClient = clientDetail.client.status === 'ACTIVE' || isComplete;

                      return (
                        <div className="space-y-3.5">
                          {/* Compact intake status row for active clients */}
                          {isActiveClient && (
                            <div className="rounded-xl border border-slate-200/90 bg-white px-4 py-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              {/* Mobile Intake Header (< sm): Centered Title + Compact Status */}
                              <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1 min-w-0">
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">Intake Packet</span>
                                  <span className={`px-2 py-0.5 text-[9px] font-mono font-bold rounded-full uppercase tracking-wider border shadow-2xs ${
                                    isComplete
                                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                      : currentStatus === 'Fully Signed'
                                      ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                                  }`}>
                                    {currentStatus}
                                  </span>
                                </div>
                                <div className="text-[10.5px] text-slate-500 flex items-center justify-center space-x-2">
                                  <span>Guardian: <strong className={guardianSigned ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>{guardianSigned ? '✓ Signed' : 'Pending'}</strong></span>
                                  <span className="text-slate-400">•</span>
                                  <span>Therapist: <strong className={therapistSigned ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>{therapistSigned ? '✓ Signed' : 'Pending'}</strong></span>
                                </div>
                              </div>

                              {/* Desktop Intake Header (>= sm): Clean Title + Status + Action Group */}
                              <div className="hidden sm:flex items-center space-x-3 min-w-0">
                                <div className="w-7 h-7 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 shrink-0 shadow-2xs">
                                  <FileText className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2.5">
                                    <span className="text-xs font-bold text-slate-900 uppercase tracking-wider truncate">Intake Packet</span>
                                    <span className={`px-2 py-0.5 text-[10px] font-mono font-bold rounded-full uppercase tracking-wider border shadow-2xs shrink-0 ${
                                      isComplete
                                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                        : currentStatus === 'Fully Signed'
                                        ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                                        : 'bg-slate-100 text-slate-700 border border-slate-200'
                                    }`}>
                                      {currentStatus}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-slate-500 flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
                                    <span>Guardian: <strong className={guardianSigned ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>{guardianSigned ? '✓ Signed' : 'Pending'}</strong></span>
                                    <span className="text-slate-400">•</span>
                                    <span>Therapist: <strong className={therapistSigned ? 'text-emerald-700 font-semibold' : 'text-slate-600'}>{therapistSigned ? '✓ Signed' : 'Pending'}</strong></span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-2.5 shrink-0 flex-wrap w-full sm:w-auto">
                                <a
                                  href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="h-7 flex-1 sm:flex-initial px-2 sm:px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-[11px] font-semibold inline-flex items-center justify-center space-x-1 sm:space-x-1.5 transition-all shadow-2xs shrink-0 whitespace-nowrap"
                                  title="View authoritative 6-page Canonical Intake Packet in browser"
                                >
                                  <Eye className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span className="sm:hidden">View</span>
                                  <span className="hidden sm:inline">View Intake Packet</span>
                                </a>

                                <a
                                  href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                  download={`Intake_Packet_${clientDetail.client.first_name}_${clientDetail.client.last_name}.pdf`}
                                  className="h-7 flex-1 sm:flex-initial px-2 sm:px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-[11px] font-semibold inline-flex items-center justify-center space-x-1 sm:space-x-1.5 transition-all shadow-2xs shrink-0 whitespace-nowrap"
                                  title="Download authoritative 6-page Canonical Intake Packet PDF"
                                >
                                  <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span className="sm:hidden">Download</span>
                                  <span className="hidden sm:inline">Download Intake Packet</span>
                                </a>

                                {intakeDoc?.esign_envelope_id && (
                                  <>
                                    <button
                                      type="button"
                                      disabled={syncingPacket}
                                      onClick={() => handleSyncIntakePacket(clientDetail.client.id)}
                                      className="h-7 px-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200/90 text-[11px] font-medium inline-flex items-center space-x-1 transition-all shrink-0"
                                      title="Troubleshooting fallback only: Reconcile SignWell status manually"
                                    >
                                      <Clock className={`w-3 h-3 text-slate-500 ${syncingPacket ? 'animate-spin' : ''}`} />
                                      <span>{syncingPacket ? 'Reconciling...' : 'Reconcile'}</span>
                                    </button>
                                    {!isComplete && (
                                      <button
                                        type="button"
                                        disabled={remindingPacket}
                                        onClick={() => handleRemindIntakePacket(clientDetail.client.id)}
                                        className="h-7 px-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-[11px] font-semibold inline-flex items-center space-x-1 transition-all shadow-2xs shrink-0"
                                        title="Send SignWell reminder to unsigned recipient(s)"
                                      >
                                        <Send className={`w-3 h-3 text-amber-700 ${remindingPacket ? 'animate-spin' : ''}`} />
                                        <span>{remindingPacket ? 'Reminding...' : 'Remind'}</span>
                                      </button>
                                    )}
                                  </>
                                )}

                                <button
                                  type="button"
                                  onClick={() => setShowIntakeLifecycle(!showIntakeLifecycle)}
                                  className="h-7 px-2 sm:px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200/90 text-[11px] inline-flex items-center justify-center space-x-1 transition-all shrink-0 whitespace-nowrap"
                                  title="Toggle dual-signer lifecycle controls"
                                >
                                  <span className="sm:hidden">{showIntakeLifecycle ? 'Hide' : 'Details'}</span>
                                  <span className="hidden sm:inline">{showIntakeLifecycle ? 'Hide Details' : 'Lifecycle Details'}</span>
                                  <ChevronDown className={`w-3 h-3 transition-transform ${showIntakeLifecycle ? 'rotate-180' : ''}`} />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Action notification message */}
                          {packetActionMsg && (
                            <div className={`p-2.5 rounded-xl text-xs flex items-center space-x-2 ${
                              packetActionMsg.type === 'success'
                                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                                : 'bg-rose-50 border border-rose-200 text-rose-800'
                            }`}>
                              {packetActionMsg.type === 'success' ? (
                                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                              )}
                              <span>{packetActionMsg.text}</span>
                            </div>
                          )}

                          {/* Full dual-signer lifecycle panel: Shown when client is not yet active, or when active client toggles details */}
                          {(!isActiveClient || showIntakeLifecycle) && (
                            <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-4 shadow-xs">
                              {/* Mobile Lifecycle Header (< sm) */}
                              <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1 border-b border-slate-200/80 pb-2.5">
                                <div className="flex items-center justify-center gap-2">
                                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                                    Intake Packet
                                  </h4>
                                  <span className={`px-2 py-0.5 text-[9px] font-mono font-bold rounded-full border uppercase tracking-wider shadow-2xs ${
                                    isComplete
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                      : currentStatus === 'Fully Signed'
                                      ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                                      : isFailedDispatch || currentStatus.toLowerCase().includes('cancelled') || currentStatus.toLowerCase().includes('reconciliation')
                                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                                      : currentStatus.includes('Signed')
                                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border-slate-200'
                                  }`}>
                                    {isFailedDispatch ? 'DISPATCH FAILED' : currentStatus}
                                  </span>
                                </div>
                              </div>

                              {/* Desktop Lifecycle Header (>= sm) */}
                              <div className="hidden sm:flex flex-row items-center justify-between gap-2 border-b border-slate-200/80 pb-2.5">
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full uppercase bg-slate-100 text-slate-700 border border-slate-200 shadow-2xs">
                                      Intake Packet
                                    </span>
                                  </div>
                                  <h4 className="text-xs font-bold text-slate-900 mt-1 flex items-center space-x-2">
                                    <FileText className="w-4 h-4 text-slate-500" />
                                    <span>Intake Packet Completion Lifecycle</span>
                                  </h4>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <span className={`px-2.5 py-1 text-xs font-bold rounded-full border uppercase tracking-wider shadow-2xs ${
                                    isComplete
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                      : currentStatus === 'Fully Signed'
                                      ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
                                      : isFailedDispatch || currentStatus.toLowerCase().includes('cancelled') || currentStatus.toLowerCase().includes('reconciliation')
                                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                                      : currentStatus.includes('Signed')
                                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border-slate-200'
                                  }`}>
                                    {isFailedDispatch ? 'DISPATCH FAILED' : currentStatus}
                                  </span>
                                </div>
                              </div>

                              {/* Dual Signers Tracking Blocks */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Signer 1: Guardian */}
                                <div className="p-3.5 bg-slate-50/70 border border-slate-200/80 rounded-xl space-y-1.5 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                      Required Signer 1: Guardian / Legal Rep
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                      guardianSigned ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'
                                    }`}>
                                      {guardianSigned ? '✓ Signed' : 'Pending Signature'}
                                    </span>
                                  </div>
                                  <div className="font-semibold text-slate-900 text-xs">
                                    {clientDetail.client.guardian_name || 'Parent / Legal Guardian'}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    eSign Email: <span className="text-slate-800 font-medium">{clientDetail.client.guardian_email || 'None on file'}</span>
                                  </div>
                                  {intakeDoc?.guardian_signature && (
                                    <div className="text-[10px] text-emerald-700 font-mono">
                                      Signed by: {intakeDoc.guardian_signature} {intakeDoc.guardian_signed_at ? `(${intakeDoc.guardian_signed_at.slice(0, 10)})` : ''}
                                    </div>
                                  )}
                                </div>

                                {/* Signer 2: Treating Therapist */}
                                <div className="p-3.5 bg-slate-50/70 border border-slate-200/80 rounded-xl space-y-1.5 shadow-2xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                      Required Signer 2: Treating Therapist
                                    </span>
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                      therapistSigned
                                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                        : isTherapistAssigned
                                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                                    }`}>
                                      {therapistSigned
                                        ? '✓ Signed'
                                        : isTherapistAssigned
                                        ? 'Pending Signature'
                                        : 'Unassigned (Blocks Send)'}
                                    </span>
                                  </div>
                                  <div className="font-semibold text-slate-900 text-xs">
                                    {clientDetail.client.assigned_provider_name || (
                                      isTherapistAssigned ? `Provider ID: ${clientDetail.client.assigned_provider_id}` : 'No Therapist Assigned'
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    Roster Email: <span className="text-slate-800 font-medium">{clientDetail.client.assigned_provider_email || (isTherapistAssigned ? 'None on file' : 'Unassigned')}</span>
                                  </div>
                                  {intakeDoc?.therapist_signature && (
                                    <div className="text-[10px] text-emerald-700 font-mono">
                                      Signed by: {intakeDoc.therapist_signature} {intakeDoc.therapist_signed_at ? `(${intakeDoc.therapist_signed_at.slice(0, 10)})` : ''}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Authoritative SignWell Dispatch Evidence & History */}
                              <div className="p-3.5 bg-slate-50/70 border border-slate-200/80 rounded-xl space-y-2 text-xs shadow-2xs">
                                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2">
                                  <div className="flex items-center space-x-2">
                                    <Shield className="w-4 h-4 text-slate-500" />
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                                      SignWell Authoritative Dispatch Evidence
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {intakeDoc?.esign_envelope_id ? (
                                      <span className="font-mono text-[10px] text-slate-700 bg-white px-2.5 py-0.5 rounded-full border border-slate-200 shadow-2xs">
                                        Envelope: {intakeDoc.esign_envelope_id}
                                      </span>
                                    ) : isFailedDispatch ? (
                                      <span className="font-semibold text-[10px] text-rose-800 bg-rose-50 px-2.5 py-0.5 rounded-full border border-rose-200 flex items-center space-x-1">
                                        <AlertCircle className="w-3 h-3 text-rose-600" />
                                        <span>Dispatch Failed (No Active Envelope)</span>
                                      </span>
                                    ) : (
                                      <span className="text-[10px] text-slate-500">Not Dispatched</span>
                                    )}
                                  </div>
                                </div>

                                {/* Authoritative Error Callout */}
                                {(intakeDoc?.esign_failure_reason || isFailedDispatch) && (
                                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-1">
                                    <div className="flex items-center space-x-1.5 text-rose-800 font-bold text-[11px]">
                                      <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                                      <span>SignWell Provider Error / Dispatch Failure:</span>
                                    </div>
                                    <div className="text-rose-900 font-mono text-[11px] bg-white p-2 rounded-lg border border-rose-200 break-all select-all">
                                      {intakeDoc?.esign_failure_reason || 'SignWell API rejected signature request. No envelope created.'}
                                    </div>
                                    <p className="text-[10px] text-rose-700">
                                      Envelope was NOT dispatched. Please edit/correct details or resend. The EMR will only mark Sent after authoritative confirmation from SignWell.
                                    </p>
                                  </div>
                                )}

                                {/* Envelope Metadata */}
                                {intakeDoc?.esign_envelope_id && (
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-slate-500">
                                    <div>
                                      <span>Provider Status: </span>
                                      <span className="text-slate-900 font-semibold">{intakeDoc.esign_status || 'SENT'}</span>
                                    </div>
                                    <div>
                                      <span>Dispatched: </span>
                                      <span className="text-slate-800 font-mono">{intakeDoc.esign_sent_at ? new Date(intakeDoc.esign_sent_at).toLocaleString() : 'Recorded'}</span>
                                    </div>
                                    <div>
                                      <span>Dual Signers: </span>
                                      <span className="text-slate-800 font-medium">Guardian & Treating Provider</span>
                                    </div>
                                  </div>
                                )}

                                {/* Dispatch History */}
                                {(() => {
                                  let dispatches: any[] = [];
                                  try {
                                    if (intakeDoc?.esign_evidence_json) {
                                      const parsed = JSON.parse(intakeDoc.esign_evidence_json);
                                      dispatches = parsed.dispatches || parsed.dispatchHistory || [];
                                    }
                                  } catch (e) {}

                                  if (!dispatches || dispatches.length === 0) return null;

                                  return (
                                    <div className="pt-2 border-t border-slate-200/80 space-y-1">
                                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        Dispatch History ({dispatches.length} Record{dispatches.length > 1 ? 's' : ''})
                                      </div>
                                      <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {dispatches.map((d: any, idx: number) => (
                                          <div key={idx} className="flex items-center justify-between text-[10px] p-2 bg-white rounded-lg border border-slate-200">
                                            <span className="text-slate-700 font-mono">{d.sentAt ? new Date(d.sentAt).toLocaleString() : 'Recent'}</span>
                                            <span className="text-slate-500 font-mono">{d.envelopeId ? `Env: ${d.envelopeId.slice(0, 14)}...` : 'No Envelope'}</span>
                                            <span className="text-slate-800 truncate max-w-[150px] font-medium">{d.recipientEmail || d.recipientName || 'Signer'}</span>
                                            <span className={`px-2 py-0.5 rounded-full font-bold uppercase ${
                                              d.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                                              d.status === 'SUPERSEDED' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
                                              d.status === 'FAILED' ? 'bg-rose-50 text-rose-800 border border-rose-200' :
                                              'bg-slate-100 text-slate-700 border border-slate-200'
                                            }`}>
                                              {d.status}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Unassigned therapist warning */}
                              {!isTherapistAssigned && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-2.5 text-amber-900 text-xs">
                                  <AlertCircle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                                  <div>
                                    <strong>Therapist Unassigned:</strong> Assign a treating therapist before sending the intake packet for eSignature. Use "Edit Chart" to assign a provider.
                                  </div>
                                </div>
                              )}

                              {/* Packet Action Controls */}
                              {/* Mobile View (< sm): Responsive Grid with Shortened Action Labels */}
                              <div className="sm:hidden grid grid-cols-2 gap-2 pt-2 border-t border-slate-200/80 w-full">
                                {/* View */}
                                <a
                                  href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label="View Intake Packet"
                                  title="View authoritative 6-page Canonical Intake Packet in browser"
                                  className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-2xs"
                                >
                                  <Eye className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>View</span>
                                </a>

                                {/* Download */}
                                <a
                                  href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                  download={`Intake_Packet_${clientDetail.client.first_name}_${clientDetail.client.last_name}.pdf`}
                                  aria-label="Download Intake Packet"
                                  title="Download authoritative 6-page Canonical Intake Packet PDF"
                                  className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-2xs"
                                >
                                  <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>Download</span>
                                </a>

                                {/* Webhook live status indicator & Fallback Sync */}
                                {intakeDoc?.esign_envelope_id && (
                                  <>
                                    {isComplete ? (
                                      <div
                                        aria-label="SignWell Webhook: Verified and updated signatures in real time"
                                        title="SignWell webhook verified and updated signatures in real time"
                                        className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-800 flex items-center justify-center space-x-1.5"
                                      >
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                        <span>Webhook</span>
                                      </div>
                                    ) : (
                                      <div
                                        aria-label="Real-Time Webhook Active: SignWell webhook will automatically record signatures and finalize on completion in real time"
                                        title="SignWell webhook will automatically record signatures and finalize on completion in real time"
                                        className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 border border-slate-200 text-xs font-medium text-slate-700 flex items-center justify-center space-x-1.5"
                                      >
                                        <span className="w-2 h-2 rounded-full bg-slate-500 animate-pulse shrink-0" />
                                        <span>Webhook</span>
                                      </div>
                                    )}

                                    <button
                                      type="button"
                                      disabled={syncingPacket}
                                      onClick={() => handleSyncIntakePacket(clientDetail.client.id)}
                                      aria-label="Reconcile SignWell envelope status"
                                      title="Troubleshooting fallback only: Manually reconcile latest SignWell envelope status if webhook delivery is delayed"
                                      className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200/90 text-xs font-medium flex items-center justify-center space-x-1.5 transition-all"
                                    >
                                      <Clock className={`w-3.5 h-3.5 shrink-0 text-slate-500 ${syncingPacket ? 'animate-spin' : ''}`} />
                                      <span>{syncingPacket ? 'Reconciling...' : 'Reconcile'}</span>
                                    </button>
                                    {!isComplete && (
                                      <button
                                        type="button"
                                        disabled={remindingPacket}
                                        onClick={() => handleRemindIntakePacket(clientDetail.client.id)}
                                        aria-label="Send reminder to unsigned recipient(s)"
                                        title="Send SignWell reminder to unsigned recipient(s)"
                                        className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-2xs"
                                      >
                                        <Send className={`w-3.5 h-3.5 shrink-0 text-amber-700 ${remindingPacket ? 'animate-spin' : ''}`} />
                                        <span>{remindingPacket ? 'Sending...' : 'Send Reminder'}</span>
                                      </button>
                                    )}
                                  </>
                                )}

                                {/* Edit Intake & Resend Action */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditIntakeClientId(clientDetail.client.id);
                                    setShowFullIntakeModal(true);
                                  }}
                                  aria-label={
                                    intakeDoc?.esign_envelope_id && !isComplete
                                      ? 'Edit Intake & Resend'
                                      : 'Edit Intake'
                                  }
                                  title={
                                    intakeDoc?.esign_envelope_id && !isComplete
                                      ? 'Edit Intake & Resend: Correct signer email or update intake data and void/resend packet'
                                      : 'Edit Intake: View or edit intake information'
                                  }
                                  className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-2xs"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                  <span>Edit</span>
                                </button>

                                {/* Dispatch via eSignature */}
                                {!isComplete && (
                                  <div className={`w-full ${packetDispatchDisabledReason ? 'col-span-2' : ''} flex flex-col gap-1`}>
                                    {packetDispatchDisabledReason && (
                                      <span className="text-[11px] text-rose-600 font-medium flex items-center space-x-1">
                                        <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                                        <span>{packetDispatchDisabledReason}</span>
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      disabled={dispatchingPacket || Boolean(packetDispatchDisabledReason)}
                                      onClick={() => handleDispatchIntakePacket(clientDetail.client.id)}
                                      aria-label="Send for eSignature"
                                      title={packetDispatchDisabledReason || 'Dispatch dual-signer packet for eSignature'}
                                      className={`w-full min-h-[38px] px-2.5 py-2 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-xs ${
                                        packetDispatchDisabledReason
                                          ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                          : 'bg-slate-800 hover:bg-slate-700 text-white'
                                      }`}
                                    >
                                      <Send className="w-3.5 h-3.5 shrink-0" />
                                      <span>{dispatchingPacket ? 'Sending...' : 'Send for eSign'}</span>
                                    </button>
                                  </div>
                                )}

                                {/* Manual Signature Verification */}
                                {!isComplete && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setVerifyName(clientDetail.client.guardian_name || 'Guardian');
                                      setShowSignVerifyModal(true);
                                    }}
                                    aria-label="Record Signature"
                                    title="Record Signature: Manual signature verification"
                                    className="w-full min-h-[38px] px-2.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all shadow-2xs"
                                  >
                                    <Edit3 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                    <span>Record Signature</span>
                                  </button>
                                )}

                                {/* Complete Intake */}
                                {!isComplete && (
                                  <button
                                    type="button"
                                    disabled={completingIntake || !canComplete}
                                    onClick={() => handleCompleteIntake(clientDetail.client.id)}
                                    aria-label="Complete Intake"
                                    title={!canComplete ? 'Both Guardian and Assigned Therapist signatures are required before intake can be marked Complete.' : 'Mark Intake Complete and Activate Client'}
                                    className={`w-full col-span-2 min-h-[40px] px-3.5 py-2 rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 transition-all ${
                                      canComplete
                                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs'
                                        : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                    }`}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                    <span>{completingIntake ? 'Completing...' : 'Complete Intake'}</span>
                                  </button>
                                )}

                                {isComplete && (
                                  <div className="w-full col-span-2 min-h-[38px] px-3 py-2 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center space-x-1.5 shadow-2xs">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                    <span>Intake Completed & Active</span>
                                  </div>
                                )}
                              </div>

                              {/* Desktop View (sm+): Clean Desktop Layout */}
                              <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-2.5 pt-3 border-t border-slate-200/80">
                                <div className="flex items-center flex-wrap gap-2.5">
                                  {/* View Intake Packet */}
                                  <a
                                    href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-2xs"
                                    title="View authoritative 6-page Canonical Intake Packet in browser"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-slate-500" />
                                    <span>View Intake Packet</span>
                                  </a>

                                  {/* Download Intake Packet */}
                                  <a
                                    href={`/api/clients/${clientDetail.client.id}/intake-packet/pdf`}
                                    download={`Intake_Packet_${clientDetail.client.first_name}_${clientDetail.client.last_name}.pdf`}
                                    className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-2xs"
                                    title="Download authoritative 6-page Canonical Intake Packet PDF"
                                  >
                                    <Download className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Download Intake Packet</span>
                                  </a>

                                  {/* Webhook live status indicator & Fallback Sync */}
                                  {intakeDoc?.esign_envelope_id && (
                                    <>
                                      {isComplete ? (
                                        <div
                                          className="px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] font-medium text-emerald-800 flex items-center space-x-1.5"
                                          title="SignWell webhook verified and updated signatures in real time"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                          <span>Auto-Updated via Webhook</span>
                                        </div>
                                      ) : (
                                        <div
                                          className="px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-700 flex items-center space-x-1.5"
                                          title="SignWell webhook will automatically record signatures and finalize on completion in real time"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-pulse" />
                                          <span>Real-Time Webhook Active</span>
                                        </div>
                                      )}

                                      <button
                                        type="button"
                                        disabled={syncingPacket}
                                        onClick={() => handleSyncIntakePacket(clientDetail.client.id)}
                                        className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200/90 text-xs font-medium flex items-center space-x-1.5 transition-all"
                                        title="Troubleshooting fallback only: Manually reconcile latest SignWell envelope status if webhook delivery is delayed"
                                      >
                                        <Clock className={`w-3.5 h-3.5 text-slate-500 ${syncingPacket ? 'animate-spin' : ''}`} />
                                        <span>{syncingPacket ? 'Reconciling...' : 'Reconcile (Fallback)'}</span>
                                      </button>
                                      {!isComplete && (
                                        <button
                                          type="button"
                                          disabled={remindingPacket}
                                          onClick={() => handleRemindIntakePacket(clientDetail.client.id)}
                                          className="px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-2xs"
                                          title="Send SignWell reminder to unsigned recipient(s)"
                                        >
                                          <Send className={`w-3.5 h-3.5 text-amber-700 ${remindingPacket ? 'animate-spin' : ''}`} />
                                          <span>{remindingPacket ? 'Sending...' : 'Send Reminder'}</span>
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2">
                                  {/* Edit Intake & Resend Action */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditIntakeClientId(clientDetail.client.id);
                                      setShowFullIntakeModal(true);
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-2xs"
                                    title={
                                      intakeDoc?.esign_envelope_id && !isComplete
                                        ? 'Correct signer email or update intake data and void/resend packet'
                                        : 'View or edit intake information'
                                    }
                                  >
                                    <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                                    <span>
                                      {intakeDoc?.esign_envelope_id && !isComplete
                                        ? 'Edit Intake & Resend'
                                        : 'Edit Intake'}
                                    </span>
                                  </button>

                                  {/* Dispatch via eSignature */}
                                  {!isComplete && (
                                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                                      {packetDispatchDisabledReason && (
                                        <span className="text-[11px] text-rose-600 font-medium flex items-center space-x-1">
                                          <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                                          <span>{packetDispatchDisabledReason}</span>
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        disabled={dispatchingPacket || Boolean(packetDispatchDisabledReason)}
                                        onClick={() => handleDispatchIntakePacket(clientDetail.client.id)}
                                        title={packetDispatchDisabledReason || 'Dispatch dual-signer packet for eSignature'}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-xs ${
                                          packetDispatchDisabledReason
                                            ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                            : 'bg-slate-800 hover:bg-slate-700 text-white'
                                        }`}
                                      >
                                        <Send className="w-3.5 h-3.5" />
                                        <span>{dispatchingPacket ? 'Sending...' : 'Send for eSignature'}</span>
                                      </button>
                                    </div>
                                  )}

                                  {/* Manual Signature Verification */}
                                  {!isComplete && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setVerifyName(clientDetail.client.guardian_name || 'Guardian');
                                        setShowSignVerifyModal(true);
                                      }}
                                      className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-2xs"
                                    >
                                      <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                                      <span>Record Signature</span>
                                    </button>
                                  )}

                                  {/* Complete Intake */}
                                  {!isComplete && (
                                    <button
                                      type="button"
                                      disabled={completingIntake || !canComplete}
                                      onClick={() => handleCompleteIntake(clientDetail.client.id)}
                                      title={!canComplete ? 'Both Guardian and Assigned Therapist signatures are required before intake can be marked Complete.' : 'Mark Intake Complete and Activate Client'}
                                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-all ${
                                        canComplete
                                          ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs'
                                          : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                                      }`}
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                      <span>{completingIntake ? 'Completing...' : 'Complete Intake'}</span>
                                    </button>
                                  )}

                                  {isComplete && (
                                    <span className="px-3 py-1 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full flex items-center space-x-1.5 shadow-2xs">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                      <span>Intake Completed & Active</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Middle Row: Clinical Speech Plan of Care (POC) & Care Team/Guardian */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Clinical & Speech POC */}
                      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-4 shadow-xs">
                        {/* Mobile Header (< sm): Centered Title + Compact Status, Secondary Edit Action Underneath */}
                        <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1.5 border-b border-slate-100 pb-2.5">
                          <div className="flex items-center justify-center gap-2">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                              {resolveClientPocStreams(clientDetail.client).mode === 'separate' ? 'Plans of Care' : 'Speech Plan of Care'}
                            </h4>
                            {resolveClientPocStreams(clientDetail.client).mode !== 'separate' && <span className={`text-[9.5px] font-mono px-2 py-0.5 rounded-full font-bold uppercase shrink-0 ${
                              clientDetail.client.poc_status === 'EXPIRED'
                                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                : clientDetail.client.poc_status === 'EXPIRING'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : clientDetail.client.poc_status === 'PENDING_PHYSICIAN' || clientDetail.client.poc_status === 'PENDING_RECERT'
                                ? 'bg-slate-100 text-slate-700 border border-slate-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}>
                              {clientDetail.client.poc_status || 'ACTIVE'}
                            </span>}
                          </div>
                          {canOperateClients && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditModalSection('poc');
                                setShowEditModal(true);
                              }}
                              className="h-6 px-2.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 text-[10.5px] font-medium inline-flex items-center space-x-1 transition-all shadow-2xs active:scale-[0.98]"
                              title="Edit plans of care"
                            >
                              <Edit3 className="w-3 h-3 text-slate-500 shrink-0" />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>

                        {/* Desktop Header (>= sm): Clean Title + Status + Action Band */}
                        <div className="hidden sm:flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
                          <div className="flex items-center space-x-2 min-w-0">
                            <div className="shrink-0">
                              <IconBadge icon={Stethoscope} variant="indigo" size="sm" />
                            </div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 truncate">
                              {resolveClientPocStreams(clientDetail.client).mode === 'separate' ? 'Plans of Care & Clinical' : 'Speech Plan of Care (POC) & Clinical'}
                            </h4>
                          </div>
                          <div className="flex items-center space-x-2 shrink-0">
                            {resolveClientPocStreams(clientDetail.client).mode !== 'separate' && <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase shrink-0 ${
                              clientDetail.client.poc_status === 'EXPIRED'
                                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                : clientDetail.client.poc_status === 'EXPIRING'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : clientDetail.client.poc_status === 'PENDING_PHYSICIAN' || clientDetail.client.poc_status === 'PENDING_RECERT'
                                ? 'bg-slate-100 text-slate-700 border border-slate-200'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}>
                              {clientDetail.client.poc_status || 'ACTIVE'}
                            </span>}
                            {canOperateClients && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditModalSection('poc');
                                  setShowEditModal(true);
                                }}
                                className="h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-[11px] font-semibold inline-flex items-center space-x-1.5 transition-all shadow-2xs active:scale-[0.98] shrink-0"
                                title="Edit plans of care"
                              >
                                <Edit3 className="w-3 h-3 text-slate-500" />
                                <span>Edit</span>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3.5 text-xs">
                          {/* Diagnosis */}
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1.5">Primary Speech Diagnosis</span>
                            <div className="flex items-center space-x-2">
                              {clientDetail.client.diagnosis_code && (
                                <span className="font-mono text-emerald-800 font-bold text-xs px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200">
                                  {clientDetail.client.diagnosis_code}
                                </span>
                              )}
                              <span className="text-slate-900 font-medium">
                                {clientDetail.client.diagnosis_description || 'Diagnosis documentation pending'}
                              </span>
                            </div>
                          </div>

                          {/* POC Period & Status */}
                          {(() => {
                            const pocView = resolveClientPocStreams(clientDetail.client);
                            if (pocView.mode !== 'separate') return null;
                            return (
                              <div className="space-y-3">
                                <ChartPocStreamCard
                                  title="Speech Plan of Care"
                                  stream={pocView.speech}
                                  onEdit={canOperateClients ? () => {
                                    setEditModalSection('speech');
                                    setShowEditModal(true);
                                  } : undefined}
                                />
                                <ChartPocStreamCard
                                  title="Feeding Plan of Care"
                                  stream={pocView.feeding}
                                  onEdit={canOperateClients ? () => {
                                    setEditModalSection('feeding');
                                    setShowEditModal(true);
                                  } : undefined}
                                />
                              </div>
                            );
                          })() || (
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 space-y-3 shadow-2xs">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[10px] uppercase font-bold text-slate-500">Speech Plan of Care (POC) Period</span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                Status: <strong className="text-slate-900">{clientDetail.client.poc_status || 'ACTIVE'}</strong>
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2.5 border-t border-slate-200/80">
                              <div>
                                <span className="text-[10px] text-slate-500 block mb-1">POC Start Date:</span>
                                <span className="font-mono text-slate-900 text-[11px] font-semibold">
                                  {clientDetail.client.poc_start_date || <span className="text-slate-400 font-normal italic">Not set</span>}
                                </span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-500 block mb-1">POC Expiration / End Date:</span>
                                <span className="font-mono text-slate-900 text-[11px] font-semibold">
                                  {clientDetail.client.poc_end_date || <span className="text-slate-400 font-normal italic">Not set</span>}
                                </span>
                              </div>
                            </div>

                            {hasUnresolvedPocDateConflict(clientDetail.client) ? (() => {
                                const startParts = splitPocDateParts(clientDetail.client.poc_start_date);
                                const endParts = splitPocDateParts(clientDetail.client.poc_end_date);
                                const orderConfirmed = Boolean(confirmedFeedingThenSpeech(clientDetail.client.id));
                                const streamHints = orderConfirmed ? ['Feeding', 'Speech'] : ['first stored date', 'second stored date'];
                                return (
                                  <div className="pt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] font-bold text-amber-900 uppercase">POC Date Conflict Flag</span>
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300">
                                        Needs Review
                                      </span>
                                    </div>
                                    <div className="text-[11px] text-amber-900 space-y-0.5">
                                      <div>
                                        <span className="text-amber-700">Competing start dates:</span>{' '}
                                        <strong className="font-mono">{clientDetail.client.poc_start_date || '—'}</strong>
                                      </div>
                                      <div>
                                        <span className="text-amber-700">Competing end dates:</span>{' '}
                                        <strong className="font-mono">{clientDetail.client.poc_end_date || '—'}</strong>
                                      </div>
                                      <div className="text-[10px] text-amber-700">
                                        {orderConfirmed
                                          ? 'Import order on this record: Feeding, then Speech. Source values are preserved until resolution.'
                                          : 'These competing dates stay unresolved. The source does not confirm which date belongs to Feeding or Speech.'}
                                      </div>
                                    </div>
                                    {canResolvePocConflict && (
                                      <div className="space-y-2">
                                        {!pocResolveOpen ? (
                                          <button
                                            type="button"
                                            onClick={() => openPocResolve(clientDetail.client)}
                                            className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md bg-amber-800 text-white hover:bg-amber-900"
                                          >
                                            Resolve Conflict
                                          </button>
                                        ) : (
                                          <div className="space-y-2 rounded-md border border-amber-300 bg-white/80 p-2">
                                            <div className="flex flex-wrap gap-1">
                                              {Array.from({ length: Math.max(startParts.length, endParts.length) }).map((_, idx) => (
                                                  <button
                                                    key={`${startParts[idx] || ''}-${endParts[idx] || ''}-${idx}`}
                                                    type="button"
                                                    className="text-[10px] px-2 py-0.5 rounded border border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100"
                                                    onClick={() => {
                                                      if (startParts[idx]) setPocResolveStart(toPocDateInputValue(startParts[idx]));
                                                      if (endParts[idx]) setPocResolveEnd(toPocDateInputValue(endParts[idx]));
                                                      setPocResolveStream(orderConfirmed ? (idx === 0 ? 'FEEDING' : idx === 1 ? 'SPEECH' : '') : '');
                                                    }}
                                                  >
                                                    Use {streamHints[idx] || `source ${idx + 1}`}: {startParts[idx] || '—'} → {endParts[idx] || '—'}
                                                  </button>
                                                ))}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <label className="text-[10px] text-amber-800">
                                                Authoritative start
                                                <input
                                                  type="date"
                                                  value={pocResolveStart}
                                                  onChange={(e) => setPocResolveStart(e.target.value)}
                                                  className="mt-0.5 w-full text-[11px] border border-amber-300 rounded px-1.5 py-1"
                                                />
                                              </label>
                                              <label className="text-[10px] text-amber-800">
                                                Authoritative end
                                                <input
                                                  type="date"
                                                  value={pocResolveEnd}
                                                  onChange={(e) => setPocResolveEnd(e.target.value)}
                                                  className="mt-0.5 w-full text-[11px] border border-amber-300 rounded px-1.5 py-1"
                                                />
                                              </label>
                                            </div>
                                            <label className="text-[10px] text-amber-800 block">
                                              Authoritative stream (optional)
                                              <select
                                                value={pocResolveStream}
                                                onChange={(e) => setPocResolveStream(e.target.value)}
                                                className="mt-0.5 w-full text-[11px] border border-amber-300 rounded px-1.5 py-1"
                                              >
                                                <option value="">Unspecified</option>
                                                <option value="SPEECH">Speech</option>
                                                <option value="FEEDING">Feeding</option>
                                              </select>
                                            </label>
                                            {pocResolveError && (
                                              <div className="text-[10px] text-rose-700">{pocResolveError}</div>
                                            )}
                                            <div className="flex gap-1.5">
                                              <button
                                                type="button"
                                                disabled={pocResolveSaving}
                                                onClick={confirmPocResolve}
                                                className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md bg-amber-800 text-white disabled:opacity-50"
                                              >
                                                {pocResolveSaving ? 'Saving…' : 'Confirm'}
                                              </button>
                                              <button
                                                type="button"
                                                disabled={pocResolveSaving}
                                                onClick={() => setPocResolveOpen(false)}
                                                className="text-[10px] uppercase px-2 py-1 rounded-md border border-amber-300 text-amber-800"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })() : clientDetail.client.poc_end_date ? (() => {
                              const diffDays = Math.ceil((new Date(clientDetail.client.poc_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                              return (
                                <div className="pt-2.5 flex items-center justify-between border-t border-slate-200/80">
                                  <span className="text-[10px] text-slate-500">Timeline / Recert:</span>
                                  <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${
                                    diffDays <= 0
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : diffDays <= 30
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : diffDays <= 60
                                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                      : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                  }`}>
                                    {diffDays <= 0 ? 'POC Expired' : `${diffDays}d remaining`}
                                  </span>
                                </div>
                              );
                            })() : null}
                          </div>
                          )}

                          {/* Physician */}
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <span className="text-[10px] uppercase font-bold text-slate-500">Ordering / Attending Physician</span>
                              {canOperateClients && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditModalSection('physician');
                                    setShowEditModal(true);
                                  }}
                                  className="h-6 px-2 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 text-[10px] font-medium inline-flex items-center space-x-1 shrink-0"
                                  title="Edit physician details"
                                >
                                  <Edit3 className="w-3 h-3 text-slate-500" />
                                  <span>Edit</span>
                                </button>
                              )}
                            </div>
                            {clientDetail.client.physician_name ? (
                              <div className="space-y-1.5 text-[11px]">
                                <div className="text-slate-900 font-semibold">{clientDetail.client.physician_name}</div>
                                <div className="text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                                  {clientDetail.client.physician_npi && <span>NPI: <strong className="font-mono text-slate-800">{clientDetail.client.physician_npi}</strong></span>}
                                  {clientDetail.client.physician_practice && <span>{clientDetail.client.physician_practice}</span>}
                                  {clientDetail.client.physician_phone && <span>Ph: {clientDetail.client.physician_phone}</span>}
                                  {clientDetail.client.physician_fax && <span>Fax: {clientDetail.client.physician_fax}</span>}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-500 text-[11px]">No physician information on file.</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Care Team & Guardian */}
                      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-4 shadow-xs">
                        {/* Mobile Header (< sm) */}
                        <div className="sm:hidden flex flex-col items-center justify-center text-center gap-2 border-b border-slate-100 pb-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                            Support Coordinator & Guardian
                          </h4>
                          {canOperateClients && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditModalSection('sc');
                                setShowEditModal(true);
                              }}
                              className="w-full max-w-[220px] h-8 px-3 rounded-xl bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 text-xs font-semibold inline-flex items-center justify-center space-x-1.5 transition-all shadow-2xs active:scale-[0.98]"
                              title="Edit Support Coordinator details"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>

                        {/* Desktop Header (>= sm): Clean Title + Action Band */}
                        <div className="hidden sm:flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
                          <div className="flex items-center space-x-2 min-w-0">
                            <div className="shrink-0">
                              <IconBadge icon={Users} variant="sky" size="sm" />
                            </div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 truncate">
                              Support Coordinator & Guardian
                            </h4>
                          </div>
                          {canOperateClients && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditModalSection('sc');
                                setShowEditModal(true);
                              }}
                              className="h-7 px-2.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-[11px] font-semibold inline-flex items-center space-x-1.5 transition-all shadow-2xs active:scale-[0.98] shrink-0"
                              title="Edit Support Coordinator details"
                            >
                              <Edit3 className="w-3 h-3 text-slate-500" />
                              <span>Edit</span>
                            </button>
                          )}
                        </div>

                        <div className="space-y-4 text-xs">
                          {/* Support Coordinator */}
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[10px] uppercase font-bold text-slate-500 block">DES/DDD Support Coordinator</span>
                            </div>
                            {clientDetail.client.support_coordinator_name ? (
                              <div className="space-y-1.5 text-[11px]">
                                <div className="text-slate-900 font-semibold">{clientDetail.client.support_coordinator_name}</div>
                                <div className="text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                                  {clientDetail.client.support_coordinator_phone && <span>Ph: {clientDetail.client.support_coordinator_phone}</span>}
                                  {clientDetail.client.support_coordinator_email && <span>Email: {clientDetail.client.support_coordinator_email}</span>}
                                  {clientDetail.client.district_code && <span>District: {clientDetail.client.district_code}</span>}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-500 text-[11px]">No DDD coordinator assigned yet.</span>
                            )}
                          </div>

                          {/* Guardian */}
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1.5">Parent / Legal Guardian</span>
                            {clientDetail.client.guardian_name || clientDetail.guardians.length > 0 ? (
                              <div className="space-y-1.5 text-[11px]">
                                <div className="text-slate-900 font-semibold">
                                  {clientDetail.client.guardian_name || clientDetail.guardians[0]?.name}
                                  <span className="text-slate-500 font-normal ml-1">
                                    ({clientDetail.client.guardian_relationship || clientDetail.guardians[0]?.relationship || 'Guardian'})
                                  </span>
                                </div>
                                <div className="text-slate-600 flex flex-wrap gap-x-3">
                                  <span>Ph: {clientDetail.client.guardian_phone || clientDetail.guardians[0]?.phone || 'None'}</span>
                                  <span>Email: {clientDetail.client.guardian_email || clientDetail.guardians[0]?.email || 'None'}</span>
                                </div>
                                {(clientDetail.client.guardian_address || clientDetail.guardians[0]?.address) && (
                                  <div className="text-slate-500 text-[10px] mt-1 pt-1 border-t border-slate-200/80">
                                    Address: {clientDetail.client.guardian_address || clientDetail.guardians[0]?.address}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500 text-[11px]">No guardian registered yet.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dedicated Therapy Caseload & Provider Assignment Card */}
                    <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3.5 shadow-xs">
                      {/* Mobile Header (< sm) */}
                      <div className="sm:hidden flex flex-col items-center justify-center text-center gap-1.5 border-b border-slate-100 pb-2.5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                          Therapy Caseload & Provider Assignment
                        </h4>
                        {canOperateClients && clientDetail.activeAssignment && (
                          <button
                            type="button"
                            onClick={handleOpenAssignModal}
                            className="h-6 px-2.5 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 text-[10.5px] font-medium inline-flex items-center space-x-1 transition-all shadow-2xs active:scale-[0.98] whitespace-nowrap"
                          >
                            <Edit3 className="w-3 h-3 text-slate-500 shrink-0" />
                            <span>Edit</span>
                          </button>
                        )}
                      </div>

                      {/* Desktop Header (>= sm): Clean Title + Action Band */}
                      <div className="hidden sm:flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5">
                        <div className="flex items-center space-x-2 min-w-0">
                          <div className="shrink-0">
                            <IconBadge icon={Users} variant="sky" size="sm" />
                          </div>
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 truncate">
                            Therapy Caseload & Provider Assignment
                          </h4>
                        </div>
                        {canOperateClients && clientDetail.activeAssignment && (
                          <button
                            type="button"
                            onClick={handleOpenAssignModal}
                            className="h-7 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold inline-flex items-center space-x-1.5 transition-all shadow-2xs active:scale-[0.98] shrink-0"
                          >
                            <Edit3 className="w-3 h-3 text-slate-500" />
                            <span>Change Assignment</span>
                          </button>
                        )}
                      </div>

                      {clientDetail.activeAssignment ? (
                        <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-0.5">Treating Clinician</span>
                            <div className="font-bold text-slate-900 text-sm">{clientDetail.activeAssignment.provider_name}</div>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-semibold inline-block mt-1">
                              {clientDetail.activeAssignment.provider_role}
                            </span>
                          </div>

                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-0.5">Supervising SLP</span>
                            {clientDetail.activeAssignment.supervisor_name ? (
                              <div>
                                <div className="font-semibold text-slate-900 text-sm">{clientDetail.activeAssignment.supervisor_name}</div>
                                <span className="text-[10px] text-slate-500">Speech Pathology Supervisor</span>
                              </div>
                            ) : (
                              <div>
                                <span className="font-semibold text-slate-700 text-sm">Direct SLP</span>
                                <span className="text-[10px] text-slate-400 block">No assistant supervision required</span>
                              </div>
                            )}
                          </div>

                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-0.5">Expected Frequency</span>
                            <div className="font-bold font-mono text-emerald-800 text-sm">{clientDetail.activeAssignment.expected_frequency || '2x/week'}</div>
                            <span className="text-[10px] text-slate-500">Scheduled cadence</span>
                          </div>

                          <div className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                            <span className="text-[10px] uppercase font-bold text-slate-500 block mb-0.5">Planned Session Duration</span>
                            <div className="font-bold font-mono text-slate-900 text-sm">{clientDetail.activeAssignment.expected_duration_minutes || 60} min</div>
                            <span className="text-[10px] text-slate-500">
                              Assigned {clientDetail.activeAssignment.assigned_date}
                            </span>
                          </div>
                        </div>
                        {(() => {
                          const remoteSignature = manualSoapApprovalDisplay(clientDetail.activeAssignment);
                          return (
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200/80 bg-slate-50/70 px-3.5 py-2.5">
                              <div className="min-w-0 text-xs">
                                <span className="text-[10px] uppercase font-bold text-slate-500 block">Remote Signature</span>
                                <div className="text-slate-900">
                                  <span className="font-bold">{remoteSignature.statusLabel}</span>
                                  {remoteSignature.reason ? (
                                    <span className="text-slate-600"> · {remoteSignature.reason}</span>
                                  ) : null}
                                </div>
                                {(remoteSignature.approvedBy || remoteSignature.approvedAt) && (
                                  <div className="text-[10px] text-slate-500">
                                    {remoteSignature.approvedBy ? `Approved by ${remoteSignature.approvedBy}` : ''}
                                    {remoteSignature.approvedBy && remoteSignature.approvedAt ? ' · ' : ''}
                                    {remoteSignature.approvedAt || ''}
                                  </div>
                                )}
                              </div>
                              {canEditRemoteSignature && (
                                <button
                                  type="button"
                                  onClick={() => setChartRemoteSignatureAssignment(clientDetail.activeAssignment)}
                                  className="h-7 px-3 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 text-xs font-semibold transition-all shadow-2xs"
                                  title="Configure Manual SOAP exception lane"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        </>
                      ) : (
                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center justify-between shadow-2xs">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                            <span>No active treating provider assigned to this client.</span>
                          </div>
                          {canOperateClients && (
                            <button
                              onClick={handleOpenAssignModal}
                              className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold transition-colors shadow-xs"
                            >
                              Assign Now
                            </button>
                          )}
                        </div>
                      )}

                      {/* Assignment History if multiple */}
                      {clientDetail.assignments && clientDetail.assignments.length > 1 && (
                        <div className="pt-2.5 border-t border-slate-100">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
                            Assignment History ({clientDetail.assignments.length})
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {clientDetail.assignments.map((hist: any) => (
                              <div
                                key={hist.id}
                                className={`text-[11px] px-2.5 py-1 rounded-lg border font-mono ${
                                  hist.active === 1
                                    ? 'bg-slate-100 border-slate-300 text-slate-900 font-bold'
                                    : 'bg-white border-slate-200 text-slate-500'
                                }`}
                              >
                                {hist.provider_name} ({hist.provider_role}) • {hist.assigned_date} {hist.active === 1 ? '• (Current)' : '• (Prior)'}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Bottom Row: Quick Highlights: Active Auths & Goals */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3 shadow-xs">
                        <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                            <IconBadge icon={Anchor} variant="sky" size="xs" />
                            <span>Active Authorizations ({clientDetail.authorizations.length})</span>
                          </h4>
                          <button
                            onClick={() => setChartTab('Auths')}
                            className="text-[11px] text-slate-600 hover:text-slate-900 font-semibold transition-colors"
                          >
                            View All Auths →
                          </button>
                        </div>
                        {clientDetail.authorizations.length === 0 ? (
                          <p className="text-xs text-slate-500 py-2">No active authorizations on file.</p>
                        ) : (
                          <div className="space-y-2">
                            {clientDetail.authorizations.map(a => (
                              <div key={a.id} className="text-xs p-3 rounded-xl bg-slate-50/70 border border-slate-200/80 flex justify-between items-center shadow-2xs">
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-mono text-slate-900 font-bold">#{a.auth_number}</span>
                                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-bold uppercase ${chartAuthorizationTypeClass(chartAuthorizationTypeLabel(a.auth_type))}`}>
                                      {chartAuthorizationTypeLabel(a.auth_type)}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-1">
                                    Service Line: {a.service_line || 'UNMAPPED'} • Valid: {a.start_date} to {a.end_date}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="text-[11px] text-slate-900 font-mono font-semibold block">
                                    {a.units_used} / {a.total_units} units
                                  </span>
                                  <span className="text-[10px] text-slate-500 font-mono">
                                    {a.available_units !== undefined ? `${a.available_units} avail` : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3 shadow-xs">
                        <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                            <IconBadge icon={Target} variant="emerald" size="xs" />
                            <span>Clinical Goals ({clientDetail.goals.length})</span>
                          </h4>
                          <button
                            onClick={() => setChartTab('Goals')}
                            className="text-[11px] text-slate-600 hover:text-slate-900 font-semibold transition-colors"
                          >
                            Manage Goals →
                          </button>
                        </div>
                        {clientDetail.goals.length === 0 ? (
                          <p className="text-xs text-slate-500 py-2">No active speech goals established yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {clientDetail.goals.map(g => (
                              <div key={g.id} className="text-xs p-3 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                                <div className="text-slate-800 font-medium line-clamp-2">{g.description}</div>
                                <div className="text-[10px] text-emerald-700 mt-1.5 flex items-center space-x-1 font-mono font-medium">
                                  <Target className="w-3 h-3" />
                                  <span>Target: {g.target_percentage}% mastery</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CLIENT NOTES (Compact Recent Notes Summary) */}
                    <div className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3 shadow-xs">
                      <div className="flex justify-between items-center pb-2.5 border-b border-slate-100">
                        <div className="flex items-center space-x-2">
                          <IconBadge icon={MessageSquare} variant="amber" size="sm" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                            Recent Notes
                          </h4>
                          <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold border border-slate-200">
                            {clientDetail.notes?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {clientDetail.notes && clientDetail.notes.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setChartTab('Notes')}
                              className="text-xs text-slate-600 hover:text-slate-900 font-semibold transition-colors px-2.5 py-1 rounded-lg hover:bg-slate-100"
                            >
                              View All →
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setClientNoteText('');
                              setClientNoteCategory('GENERAL');
                              setClientNoteError(null);
                              setShowAddClientNoteModal(true);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold flex items-center space-x-1.5 transition-all shadow-xs"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add Note</span>
                          </button>
                        </div>
                      </div>

                      {/* Recent Notes Stream (Newest 3) */}
                      {!clientDetail.notes || clientDetail.notes.length === 0 ? (
                        <p className="text-xs text-slate-500 py-3 italic">
                          No operational notes recorded for this client.
                        </p>
                      ) : (
                        <div className="space-y-2.5">
                          {clientDetail.notes.slice(0, 3).map(note => (
                            <div
                              key={note.id}
                              className="p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/80 space-y-1.5 shadow-2xs"
                            >
                              <div className="flex items-center justify-between text-[11px]">
                                <div className="flex items-center space-x-2">
                                  <span className="font-semibold text-slate-900">
                                    {note.user_name || 'Staff User'}
                                  </span>
                                  {note.user_role && (
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                                      {note.user_role}
                                    </span>
                                  )}
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full uppercase font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                    {note.category || 'GENERAL'}
                                  </span>
                                  {note.source && note.source !== 'CLIENT_OVERVIEW' && (
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full uppercase text-slate-500 bg-slate-100 border border-slate-200">
                                      {note.source}
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] font-mono text-slate-400">
                                  {new Date(note.created_at).toLocaleString([], {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {note.note}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 2. AUTHS TAB (Report 11281 Authentic Fidelity) */}
                {chartTab === 'Auths' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                          <IconBadge icon={Anchor} variant="sky" size="xs" />
                          <span>Authorizations (WellSky Report 11281 / DDD Focus)</span>
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Authentic operational authorization records tracking units, service codes, and dates.
                        </p>
                      </div>
                      {!isClinician && (
                        <button
                          onClick={() => setShowNewAuthModal(true)}
                          className="px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-xs flex items-center space-x-1.5 self-start sm:self-auto"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Add Authorization</span>
                        </button>
                      )}
                    </div>

                    {clientDetail.authorizations.length > 0 && (() => {
                      const flags = currentAuthorizationFlags(clientDetail.authorizations);
                      const feedingStatus = feedingAuthorizationStatusText(flags);

                      return (
                        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 space-y-2.5 shadow-xs">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                              <IconBadge icon={ShieldCheck} variant="sky" size="xs" />
                              <span>Clinical Authorization Status</span>
                            </span>
                            <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
                              <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                                flags.hasSea ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}>
                                SEA (Eval): {flags.hasSea ? 'ACTIVE' : 'NONE'}
                              </span>
                              <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                                flags.hasSta ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}>
                                STA (Tx): {flags.hasSta ? 'ACTIVE' : 'NONE'}
                              </span>
                              <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                                flags.hasFea ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}>
                                FEA (Eval): {flags.hasFea ? 'ACTIVE' : 'NONE'}
                              </span>
                              <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                                flags.hasFta ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                              }`}>
                                FTA (Tx): {flags.hasFta ? 'ACTIVE' : 'NONE'}
                              </span>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-600 leading-relaxed">
                            {speechAuthorizationStatusText(flags)}
                          </p>
                          {feedingStatus && (
                            <p className="text-[11px] text-slate-600 leading-relaxed">
                              {feedingStatus}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {clientDetail.authorizations.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No authorizations attached to this client. Click '+ Add Authorization' above.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.authorizations.map(auth => {
                          const percentUsed = Math.min(100, Math.round((auth.units_used / (auth.total_units || 1)) * 100));
                          const diffDays = Math.ceil((new Date(auth.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                          const isExpired = diffDays <= 0 || auth.status === 'EXPIRED';
                          const authTypeLabel = chartAuthorizationTypeLabel(auth.auth_type);

                          return (
                            <div key={auth.id} className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3.5 shadow-xs transition-all">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-mono text-sm font-bold text-slate-900">
                                      Auth #{auth.auth_number}
                                    </span>
                                    <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${chartAuthorizationTypeClass(authTypeLabel)}`}>
                                      {authTypeLabel}
                                    </span>
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full font-medium uppercase bg-slate-100 text-slate-700 border border-slate-200">
                                      {auth.service_line || 'UNMAPPED'}
                                    </span>
                                    {auth.focus_auth_id && (
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200" title="Focus Auth ID">
                                        Focus: {auth.focus_auth_id}
                                      </span>
                                    )}
                                    {auth.wellsky_auth_id && (
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200" title="WellSky Auth ID">
                                        WS: {auth.wellsky_auth_id}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-800 font-semibold mt-1">
                                    {authorizationServiceDescription(auth)} ({auth.focus_service_code || auth.service_code})
                                  </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${
                                    isExpired
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : auth.status === 'ACTIVE'
                                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                      : auth.status === 'EXHAUSTED'
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : 'bg-amber-50 text-amber-800 border border-amber-200'
                                  }`}>
                                    {isReplacedAuthorization(auth) ? 'REPLACED' : auth.status}
                                  </span>
                                  <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase ${
                                    isExpired
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : diffDays <= 30
                                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                      : diffDays <= 60
                                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                                  }`}>
                                    {isExpired ? 'Expired' : `${diffDays}d left`}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
                                <div className="p-2.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                                  <span className="text-slate-500 block text-[10px]">Validity Period</span>
                                  <span className="font-mono text-slate-900 font-medium">{auth.start_date} → {auth.end_date}</span>
                                </div>
                                <div className="p-2.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                                  <span className="text-slate-500 block text-[10px]">Payer / Source</span>
                                  <span className="text-slate-900 font-medium">{auth.payer_name}</span>
                                </div>
                                <div className="p-2.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                                  <span className="text-slate-500 block text-[10px]">Unit Type</span>
                                  <span className="font-mono text-slate-900 font-medium">{auth.unit_type || '15 MIN / VISIT'}</span>
                                </div>
                                <div className="p-2.5 rounded-xl bg-slate-50/70 border border-slate-200/80 shadow-2xs">
                                  <span className="text-slate-500 block text-[10px]">Available Units</span>
                                  <span className="font-mono text-emerald-800 font-bold">
                                    {displayAvailableUnits(auth.total_units, auth.units_used, auth.available_units ?? null)}
                                  </span>
                                </div>
                              </div>

                              {/* Unit consumption progress bar */}
                              <div className="pt-1">
                                <div className="flex justify-between text-[11px] mb-1.5">
                                  <span className="text-slate-500">Unit Consumption</span>
                                  <span className="text-slate-900 font-mono font-semibold">
                                    {auth.units_used} / {auth.total_units} units used ({percentUsed}%)
                                  </span>
                                </div>
                                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden border border-slate-200/80 p-[1px]">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      percentUsed > 85 ? 'bg-rose-500' : percentUsed > 60 ? 'bg-amber-500' : 'bg-emerald-500'
                                    }`}
                                    style={{ width: `${percentUsed}%` }}
                                  />
                                </div>
                              </div>

                              {isOwner && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                  {isOpenManualBridge(auth) && (
                                    <button
                                      type="button"
                                      onClick={() => setBridgePanel({ mode: 'replace', authId: auth.id })}
                                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-bold text-slate-800"
                                    >
                                      Replace bridge
                                    </button>
                                  )}
                                  {isImportedAuthorization(auth) && (
                                    <button
                                      type="button"
                                      onClick={() => setBridgePanel({ mode: 'correct', authId: auth.id })}
                                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-bold text-slate-800"
                                    >
                                      Correct current usage
                                    </button>
                                  )}
                                </div>
                              )}

                              {(auth.support_coordinator || auth.provider_vendor) && (
                                <div className="text-[10px] text-slate-500 pt-1 flex flex-wrap gap-x-4">
                                  {auth.support_coordinator && <span>SC: <strong className="text-slate-700">{auth.support_coordinator}</strong></span>}
                                  {auth.provider_vendor && <span>Vendor: <strong className="text-slate-700">{auth.provider_vendor}</strong></span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {bridgePanel && clientDetail && (
                      <BridgeAuthorizationPanel
                        mode={bridgePanel.mode}
                        clientId={clientDetail.client.id}
                        initialAuthId={bridgePanel.authId}
                        authorizations={clientDetail.authorizations}
                        services={clientDetail.services}
                        refreshNonce={bridgeRefreshNonce}
                        onClose={() => setBridgePanel(null)}
                        onChartUpdated={() => fetchClientDetail(clientDetail.client.id)}
                        onRefreshEvidence={async () => {
                          await fetchClientDetail(clientDetail.client.id);
                          setBridgeRefreshNonce((nonce) => nonce + 1);
                        }}
                      />
                    )}
                  </div>
                )}

                {/* 3. GOALS TAB */}
                {chartTab === 'Goals' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-100">
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                          <IconBadge icon={Target} variant="emerald" size="xs" />
                          <span>Canonical Speech & Language Goals History</span>
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-1">
                          A licensed SLP can add, replace, meet, or discontinue goals here. Evaluation and Plan of Care finalization still apply their own goal actions.
                        </p>
                      </div>
                      {canManageCanonicalGoals && (
                        <button
                          type="button"
                          onClick={() => openGoalEditor({ mode: 'add' })}
                          className="h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center space-x-1.5"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Add Goal</span>
                        </button>
                      )}
                    </div>

                    {canManageCanonicalGoals && goalEditor && (
                      <form
                        className="p-3.5 rounded-2xl border border-slate-200 bg-slate-50 space-y-2"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void saveCanonicalGoal(goalEditor.mode === 'modify' ? 'MODIFY' : 'ADD', goalEditor.mode === 'modify' ? goalEditor.goal.id : undefined);
                        }}
                      >
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                          {goalEditor.mode === 'modify' ? `Replace ${goalEditor.goal.id}` : 'New canonical goal'}
                        </div>
                        {goalEditor.mode === 'modify' && (
                          <p className="text-[11px] text-slate-500">The current wording stays on the superseded goal. This text becomes the successor.</p>
                        )}
                        <textarea
                          required
                          value={goalForm.description}
                          onChange={(event) => setGoalForm({ ...goalForm, description: event.target.value })}
                          placeholder="Goal text"
                          rows={2}
                          className="w-full rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-900"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                          <input value={goalForm.baseline} onChange={(event) => setGoalForm({ ...goalForm, baseline: event.target.value })} placeholder="Baseline" className="rounded-lg border border-slate-200 bg-white p-2 text-xs" />
                          <input value={goalForm.current} onChange={(event) => setGoalForm({ ...goalForm, current: event.target.value })} placeholder="Current %" className="rounded-lg border border-slate-200 bg-white p-2 text-xs" />
                          <input value={goalForm.target} onChange={(event) => setGoalForm({ ...goalForm, target: event.target.value })} placeholder="Target %" className="rounded-lg border border-slate-200 bg-white p-2 text-xs" />
                          <input value={goalForm.targetDate} onChange={(event) => setGoalForm({ ...goalForm, targetDate: event.target.value })} placeholder="Target date" className="rounded-lg border border-slate-200 bg-white p-2 text-xs" />
                        </div>
                        {goalError && <p className="text-[11px] text-rose-700">{goalError}</p>}
                        <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => openGoalEditor(null)} className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
                          <button type="submit" disabled={goalSaving} className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">{goalSaving ? 'Saving...' : 'Save goal'}</button>
                        </div>
                      </form>
                    )}
                    {goalError && !goalEditor && <p className="text-[11px] text-rose-700">{goalError}</p>}

                    {clientDetail.goals.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No canonical goals yet. An SLP can add the first goal here, or an Evaluation or Plan of Care can create them when finalized.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.goals.map((g) => (
                          <div key={g.id} className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-2.5 shadow-xs transition-all">
                            <div className="flex justify-between items-start gap-2">
                              <div className="space-y-1">
                                <span className="text-xs font-semibold text-slate-900 leading-relaxed block">{g.description}</span>
                                <div className="text-[10px] font-mono text-slate-400">ID: {g.id}</div>
                              </div>
                              <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                                {canManageCanonicalGoals && (g.status === 'ACTIVE' || g.status === 'IN_PROGRESS') && (
                                  <>
                                    <button type="button" onClick={() => openGoalEditor({ mode: 'modify', goal: g })} className="h-7 px-2 rounded-md border border-slate-200 bg-white text-[10px] font-semibold text-slate-700">Modify</button>
                                    <button type="button" onClick={() => void saveCanonicalGoal('MET', g.id)} className="h-7 px-2 rounded-md border border-emerald-200 bg-emerald-50 text-[10px] font-semibold text-emerald-800">Mark Met</button>
                                    <button type="button" onClick={() => void saveCanonicalGoal('DISCONTINUE', g.id)} className="h-7 px-2 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-700">Discontinue</button>
                                  </>
                                )}
                                <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full border font-bold uppercase ${
                                  g.status === 'MET' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                  g.status === 'DISCONTINUED' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                                  g.status === 'SUPERSEDED' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  'bg-sky-50 text-sky-700 border-sky-200'
                                }`}>
                                  {g.status}
                                </span>
                              </div>
                            </div>
                            <div className="text-[11px] text-slate-600 flex flex-wrap items-center gap-3 pt-1 border-t border-slate-100">
                              <div><span className="text-slate-400">Baseline:</span> {g.baseline_performance || 'N/A'}</div>
                              {g.origin_doc_id && (
                                <div className="text-[10px] font-mono text-slate-600">
                                  Origin Doc: <span className="text-slate-900 font-semibold">{g.origin_doc_id}</span>
                                </div>
                              )}
                              {g.parent_goal_id && (
                                <div className="text-[10px] font-mono text-amber-800">
                                  Successor of: <span className="underline font-semibold">{g.parent_goal_id}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 text-xs pt-1 font-mono">
                              <div className="flex items-center space-x-1.5 text-emerald-800 font-semibold">
                                <Target className="w-3.5 h-3.5 text-emerald-600" />
                                <span>Target: {g.target_percentage}% mastery</span>
                              </div>
                              <div className="flex items-center space-x-1.5 text-slate-700">
                                <span>Current: {g.current_percentage !== undefined ? `${g.current_percentage}%` : 'Pending Data'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 4. VISITS / SERVICES TAB */}
                {chartTab === 'Visits' && (
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-slate-100">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                        <IconBadge icon={Calendar} variant="sky" size="xs" />
                        <span>Scheduled & Completed Sessions</span>
                      </h3>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Session roster, billing readiness checks, and clinical documentation status.
                      </p>
                    </div>
                    {clientDetail.services.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No service visits recorded for this client.
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xs">
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                              <tr>
                                <th className="px-4 py-3 font-bold">DOS</th>
                                <th className="px-4 py-3 font-bold">Provider</th>
                                <th className="px-4 py-3 font-bold">CPT</th>
                                <th className="px-4 py-3 font-bold">Status</th>
                                <th className="px-4 py-3 font-bold">Readiness</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {clientDetail.services.map((s) => (
                                <tr key={s.id} className="hover:bg-slate-50/70 transition-colors group cursor-pointer">
                                  <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{s.dos}</td>
                                  <td className="px-4 py-3 text-slate-700 font-medium">{s.provider_name}</td>
                                  <td className="px-4 py-3 font-mono text-slate-800 font-medium">{s.service_code || '—'}</td>
                                  <td className="px-4 py-3">
                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                      {s.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {String(s.status || '').toUpperCase() === 'COMPLETED' ? (
                                      <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold ${
                                        s.readiness_status === 'BILLING_READY'
                                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                          : 'bg-amber-50 text-amber-800 border border-amber-200'
                                      }`}>
                                        {visitReadinessLabel(s.status, s.readiness_status)}
                                      </span>
                                    ) : (
                                      <span className="text-[10px] font-mono text-slate-400">{visitReadinessLabel(s.status, s.readiness_status)}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. CLINICAL DOCS TAB */}
                {chartTab === 'Clinical Docs' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                          <IconBadge icon={FileText} variant="indigo" size="xs" />
                          <span>Clinical Notes, SOAPs & Documents</span>
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-1">
                          SOAP notes, physician-signed plans of care, evaluations, and external clinical records
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setUploadDocForm(prev => ({
                            ...prev,
                            poc_end_date: clientDetail.client.poc_end_date || '',
                            doc_type: 'EXTERNAL'
                          }));
                          setShowUploadDocModal(true);
                        }}
                        className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-xs self-start sm:self-auto"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Upload Document</span>
                      </button>
                    </div>

                    {clientDetail.documents.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No clinical documentation generated or uploaded for this client yet.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.documents.map((doc) => {
                          const dt = (doc.doc_type || '').toUpperCase();
                          const titleLower = (doc.title || '').toLowerCase();
                          const isIntakePacket = dt === 'INTAKE_PACKET' || dt === 'DDD_PACKET' || titleLower.includes('intake packet');
                          const isPhysSigned = isPhysicianSignedCpoc(doc);
                          const isFeeding = isFeedingClinicalDoc(doc);
                          const isPoc = !isPhysSigned && !isIntakePacket && (dt === 'POC' || dt === 'FEEDING_POC' || titleLower.includes('plan of care'));
                          const badgeLabel = formatClinicalDocTypeLabel(doc.doc_type, doc.title);
                          const pocLife = derivePocActiveExpired(doc.poc_end_date);
                          const clinicianSig = clinicianSigDisplay(doc);
                          const guardianSig = guardianSigDisplay(doc);

                          const badgeColor = isPhysSigned
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : isPoc && isFeeding
                            ? 'bg-orange-50 text-orange-800 border border-orange-200'
                            : isPoc
                            ? 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                            : dt === 'INTAKE_PACKET'
                            ? 'bg-teal-50 text-teal-800 border border-teal-200'
                            : dt === 'DDD_PACKET'
                            ? 'bg-amber-50 text-amber-800 border border-amber-200'
                            : dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION'
                            ? 'bg-teal-50 text-teal-900 border border-teal-200'
                            : dt === 'EVAL'
                            ? 'bg-sky-50 text-sky-800 border border-sky-200'
                            : dt === 'QPR'
                            ? 'bg-purple-50 text-purple-800 border border-purple-200'
                            : 'bg-slate-100 text-slate-700 border border-slate-200';

                          return (
                            <div key={doc.id} className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-3.5 shadow-xs transition-all">
                              <div className="flex flex-wrap justify-between items-start gap-2">
                                <div className="space-y-1">
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${badgeColor}`}>
                                      {badgeLabel}
                                    </span>
                                    {doc.source && (
                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono border ${
                                        doc.source === 'INCOMING_FAX'
                                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                          : 'bg-slate-100 text-slate-600 border-slate-200'
                                      }`}>
                                        {doc.source === 'INCOMING_FAX' ? 'Faxage Inbound' : doc.source === 'MANUAL_UPLOAD' ? 'Direct Upload' : doc.source}
                                      </span>
                                    )}
                                    <span className="font-bold text-sm text-slate-900">
                                      {doc.title || defaultClinicalUploadTitle(doc.doc_type)}
                                    </span>
                                  </div>
                                <div className="flex items-center space-x-3 text-xs text-slate-500 font-mono pt-1">
                                  {doc.dos && <span>DOS: {doc.dos}</span>}
                                  {doc.document_date && <span>Date: {doc.document_date}</span>}
                                  {doc.poc_end_date && (
                                    <span className="text-amber-800 font-semibold">POC Exp: {doc.poc_end_date}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase border border-slate-200">
                                  {doc.status}
                                </span>
                                {isPhysSigned && pocLife && (
                                  <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full font-bold uppercase border ${
                                    pocLife === 'Expired'
                                      ? 'bg-rose-50 text-rose-700 border-rose-200'
                                      : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                  }`}>
                                    {pocLife}
                                  </span>
                                )}
                                <a
                                  href={`/api/clinical/docs/${doc.id}/pdf`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2.5 py-1 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 rounded-lg text-[11px] font-semibold transition-all flex items-center space-x-1 shadow-2xs"
                                >
                                  <FileText className="w-3 h-3 text-slate-500" />
                                  <span>View PDF</span>
                                </a>
                                <a
                                  href={`/api/clinical/docs/${doc.id}/download`}
                                  download
                                  className="px-2.5 py-1 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200/90 rounded-lg text-[11px] font-semibold transition-all flex items-center space-x-1 shadow-2xs"
                                >
                                  <Download className="w-3 h-3 text-slate-500" />
                                  <span>Download</span>
                                </a>
                              </div>
                            </div>

                            {/* Attached File Metadata if Uploaded */}
                            {doc.file_name && (
                              <div className="flex items-center space-x-2 p-2.5 rounded-xl bg-slate-50/70 border border-slate-200/80 text-xs text-slate-700 shadow-2xs">
                                <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                                <span className="font-medium text-slate-900">{doc.file_name}</span>
                                {doc.file_size ? (
                                  <span className="text-[10px] text-slate-400">
                                    ({Math.round(doc.file_size / 1024)} KB)
                                  </span>
                                ) : null}
                              </div>
                            )}

                            {/* Notes / Annotation */}
                            {doc.notes && (
                              <div className="text-xs text-slate-600 italic bg-slate-50/70 p-2.5 rounded-xl border border-slate-200/80">
                                Annotation: {doc.notes}
                              </div>
                            )}

                            {/* Clinical Content if SOAP / POC text present (strictly excluded for intake packets) */}
                            {!isIntakePacket && (doc.subjective || doc.objective || doc.assessment || doc.plan) && (
                              <div className="text-xs text-slate-700 space-y-1.5 bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80 shadow-2xs">
                                {doc.subjective && <div><span className="text-slate-500 font-semibold">Subjective:</span> {doc.subjective}</div>}
                                {doc.objective && <div><span className="text-slate-500 font-semibold">Objective:</span> {doc.objective}</div>}
                                {doc.assessment && <div><span className="text-slate-500 font-semibold">Assessment:</span> {doc.assessment}</div>}
                                {doc.plan && <div><span className="text-slate-500 font-semibold">Plan:</span> {doc.plan}</div>}
                              </div>
                            )}

                            {isIntakePacket ? (
                              <div className="flex flex-wrap gap-4 text-[11px] pt-1 text-slate-500 border-t border-slate-100">
                                <div>
                                  Packet Status: <span className="text-slate-900 font-medium">{doc.intake_status || 'Ready for Dual eSign'}</span>
                                </div>
                                <div>
                                  Dual eSign: <span className="text-slate-900 font-medium">{doc.esign_status || 'SignWell Envelope Initiated'}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-4 text-[11px] pt-1 text-slate-500 border-t border-slate-100">
                                {clinicianSig && (
                                  <div>
                                    Clinician Sig: <span className="text-slate-900 font-medium">{clinicianSig}</span>
                                  </div>
                                )}
                                {guardianSig && (
                                  <div>
                                    Guardian Sig: <span className="text-slate-900 font-medium">{guardianSig}</span>
                                  </div>
                                )}
                                {(doc.physician_signature || doc.physician_name) && (
                                  <div className="text-emerald-800">
                                    Physician Sig:{' '}
                                    <span className="font-medium text-emerald-900">
                                      {doc.physician_signature || doc.physician_name}
                                    </span>
                                    {doc.physician_signed_at ? (
                                      <span className="text-emerald-700 font-normal"> ({String(doc.physician_signed_at).slice(0, 10)})</span>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </div>
                )}

                {/* 6. BILLING TAB (Non-clinical only) */}
                {chartTab === 'Billing' && !isClinicalStaff && (
                  <div className="space-y-4">
                    <div className="pb-3 border-b border-slate-100">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                        <IconBadge icon={CircleDollarSign} variant="emerald" size="xs" />
                        <span>Claims & Payments History</span>
                      </h3>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Historical claims generated, reimbursement remittance status, and paid balances.
                      </p>
                    </div>
                    {clientDetail.billing.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No claims created yet for this client.
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xs">
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                              <tr>
                                <th className="px-4 py-3 font-bold">Claim #</th>
                                <th className="px-4 py-3 font-bold">Payer</th>
                                <th className="px-4 py-3 font-bold">Billed</th>
                                <th className="px-4 py-3 font-bold">Status</th>
                                <th className="px-4 py-3 font-bold">Paid</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {clientDetail.billing.map((b) => (
                                <tr key={b.id} className="hover:bg-slate-50/70 transition-colors">
                                  <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{b.claim_number}</td>
                                  <td className="px-4 py-3 text-slate-700 font-medium">{b.payer_name}</td>
                                  <td className="px-4 py-3 font-mono text-slate-900">${b.billed_amount?.toFixed(2)}</td>
                                  <td className="px-4 py-3">
                                    <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 uppercase font-bold">
                                      {b.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 font-mono text-emerald-800 font-bold">
                                    ${b.amount_paid?.toFixed(2) || '0.00'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 7. NOTES TAB (Canonical client_notes stream) */}
                {chartTab === 'Notes' && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                      <div>
                        <div className="flex items-center space-x-2">
                          <IconBadge icon={MessageSquare} variant="amber" size="xs" />
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                            Client Operational Notes
                          </h3>
                          <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold border border-slate-200">
                            {clientDetail.notes?.length || 0}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Unified operational record of administrative, coordination, and phone call notes
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setClientNoteText('');
                          setClientNoteCategory('GENERAL');
                          setClientNoteError(null);
                          setShowAddClientNoteModal(true);
                        }}
                        className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all shadow-xs self-start sm:self-auto"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Add Note</span>
                      </button>
                    </div>

                    {!clientDetail.notes || clientDetail.notes.length === 0 ? (
                      <div className="p-8 text-center text-xs text-slate-500 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                        No operational notes recorded for this client yet.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientDetail.notes.map(note => (
                          <div
                            key={note.id}
                            className="rounded-2xl border border-slate-200/90 bg-white p-5 space-y-2.5 shadow-xs transition-all"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs border-b border-slate-100 pb-2.5">
                              <div className="flex items-center space-x-2">
                                <span className="font-semibold text-slate-900">
                                  {note.user_name || 'Staff User'}
                                </span>
                                {note.user_role && (
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                                    {note.user_role}
                                  </span>
                                )}
                                <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full uppercase font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                  {note.category || 'GENERAL'}
                                </span>
                                {note.source && note.source !== 'CLIENT_OVERVIEW' && (
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full uppercase text-slate-500 bg-slate-100 border border-slate-200">
                                    {note.source}
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] font-mono text-slate-400">
                                {new Date(note.created_at).toLocaleString([], {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                              {note.note}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* MODAL: Full Allegiant Intake & Service Agreement */}
      {showFullIntakeModal && (
        <FullIntakeModal
          isOpen={showFullIntakeModal}
          editClientId={editIntakeClientId || undefined}
          hasPriorEnvelope={Boolean(
            editIntakeClientId &&
            clientDetail?.client?.id === editIntakeClientId &&
            (clientDetail.documents?.some(
              (d: any) => d.doc_type === 'INTAKE_PACKET' && d.esign_envelope_id
            ) || (clientDetail as any).intakeDoc?.esign_envelope_id)
          )}
          onClose={() => {
            setShowFullIntakeModal(false);
            setEditIntakeClientId(null);
          }}
          onSuccess={async () => {
            setShowFullIntakeModal(false);
            setEditIntakeClientId(null);
            await fetchClients();
            if (selectedClientId) {
              await fetchClientDetail(selectedClientId);
            }
          }}
          onClientCreated={async (newId) => {
            setShowFullIntakeModal(false);
            setEditIntakeClientId(null);
            await fetchClients();
            onSelectClient(newId);
          }}
        />
      )}

      {/* MODAL: Record Verified Signature */}
      {showSignVerifyModal && selectedClientId && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center space-x-2">
                <Edit3 className="w-4 h-4 text-slate-700" />
                <span>Record In-Office / Verified Signature</span>
              </h3>
              <button onClick={() => setShowSignVerifyModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-600 font-semibold mb-1">Signer Role *</label>
                <select
                  value={verifyRole}
                  onChange={(e) => setVerifyRole(e.target.value as any)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="GUARDIAN">Guardian / Legal Representative</option>
                  <option value="THERAPIST">Assigned Treating Therapist</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">Full Legal Name of Signer *</label>
                <input
                  type="text"
                  required
                  value={verifyName}
                  onChange={(e) => setVerifyName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="e.g. Maria Gonzalez or Justin Ross, MS, CCC-SLP"
                />
              </div>

              <p className="text-[11px] text-slate-500">
                Recording this signature indicates authoritative execution of the Allegiant Client Intake Packet & Service Agreement.
              </p>

              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowSignVerifyModal(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!verifyName.trim()}
                  onClick={() => handleRecordSignature(selectedClientId, verifyRole, verifyName.trim())}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold transition shadow-xs disabled:opacity-50"
                >
                  Record Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Intake New Client */}
      {canOperateClients && showNewClientModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-xl w-full p-6 space-y-4 shadow-xl">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
                Intake Client Record
              </h3>
              <button onClick={() => setShowNewClientModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateClient} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    value={newClientForm.first_name}
                    onChange={(e) => setNewClientForm({ ...newClientForm, first_name: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                    placeholder="e.g. Elias"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={newClientForm.last_name}
                    onChange={(e) => setNewClientForm({ ...newClientForm, last_name: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                    placeholder="e.g. Anderson"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Date of Birth *</label>
                  <input
                    type="date"
                    required
                    value={newClientForm.dob}
                    onChange={(e) => setNewClientForm({ ...newClientForm, dob: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Gender</label>
                  <select
                    value={newClientForm.gender}
                    onChange={(e) => setNewClientForm({ ...newClientForm, gender: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-1">Service / Treatment Address</label>
                <input
                  type="text"
                  value={newClientForm.service_address}
                  onChange={(e) => setNewClientForm({ ...newClientForm, service_address: e.target.value })}
                  className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  placeholder="e.g. 4208 W Camelback Rd, Phoenix, AZ 85019"
                />
              </div>

              <div className="p-2.5 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-slate-800">DDD Program Context</span>
                  <p className="text-[10px] text-slate-500">Allegiant client default context (separate from billing payer)</p>
                </div>
                <label className="flex items-center space-x-2 cursor-pointer text-xs text-slate-700 font-medium">
                  <input
                    type="checkbox"
                    checked={newClientForm.is_ddd !== false}
                    onChange={(e) => setNewClientForm({ ...newClientForm, is_ddd: e.target.checked })}
                    className="rounded border-slate-300 text-slate-800 focus:ring-slate-500"
                  />
                  <span>DDD = YES (Default)</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Primary Payer</label>
                  <select
                    value={newClientForm.primary_payer_name}
                    onChange={(e) => setNewClientForm({ ...newClientForm, primary_payer_name: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="Arizona Medicaid (Mercy Care)">Arizona Medicaid (Mercy Care)</option>
                    <option value="DES/DDD">DES/DDD — Arizona Division of Developmental Disabilities</option>
                    <option value="Arizona Complete Health">Arizona Complete Health</option>
                    <option value="Banner University Family Care">Banner University Family Care</option>
                    <option value="UnitedHealthcare Community Plan">UnitedHealthcare Community Plan</option>
                    <option value="Blue Cross Blue Shield of Arizona">Blue Cross Blue Shield of Arizona</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Medicaid / Policy ID</label>
                  <input
                    type="text"
                    value={newClientForm.policy_id}
                    onChange={(e) => setNewClientForm({ ...newClientForm, policy_id: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                    placeholder="e.g. A92019284"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3 space-y-3">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  Guardian / Legal Representative
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Guardian Name</label>
                    <input
                      type="text"
                      value={newClientForm.guardian_name}
                      onChange={(e) => setNewClientForm({ ...newClientForm, guardian_name: e.target.value })}
                      className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                      placeholder="e.g. Maria Anderson"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Guardian Phone</label>
                    <input
                      type="text"
                      value={newClientForm.guardian_phone}
                      onChange={(e) => setNewClientForm({ ...newClientForm, guardian_phone: e.target.value })}
                      className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-xs transition-colors"
                >
                  Save Client Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Add Authorization */}
      {showNewAuthModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
                Add Authorization (WellSky Import)
              </h3>
              <button onClick={() => setShowNewAuthModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateAuth} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-600 font-medium mb-1">Authorization Number *</label>
                <input
                  type="text"
                  required
                  value={newAuthForm.auth_number}
                  onChange={(e) => setNewAuthForm({ ...newAuthForm, auth_number: e.target.value })}
                  className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  placeholder="e.g. AUTH-AZ-99214"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Start Date</label>
                  <input
                    type="date"
                    required
                    value={newAuthForm.start_date}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, start_date: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">End Date</label>
                  <input
                    type="date"
                    required
                    value={newAuthForm.end_date}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, end_date: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">CPT Service Code</label>
                  <select
                    value={newAuthForm.service_code}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, service_code: e.target.value })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="92507">92507 (Speech Therapy Individual)</option>
                    <option value="92523">92523 (Speech-Language Eval)</option>
                    <option value="92508">92508 (Group Therapy)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Total Approved Units</label>
                  <input
                    type="number"
                    required
                    value={newAuthForm.total_units}
                    onChange={(e) => setNewAuthForm({ ...newAuthForm, total_units: parseInt(e.target.value) || 32 })}
                    className="w-full bg-slate-50/70 border border-slate-200 text-slate-800 rounded-lg p-2 text-xs focus:outline-hidden focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowNewAuthModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-xs transition-colors"
                >
                  Save Authorization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Edit Modal */}
      {canOperateClients && showEditModal && clientDetail && (
        <ClientEditModal
          key={clientDetail.client.id}
          client={clientDetail.client}
          initialFocusSection={editModalSection}
          onSave={(updated) => {
            setClientDetail({
              ...clientDetail,
              client: updated
            });
            setShowEditModal(false);
            setEditModalSection('general');
            fetchClients();
          }}
          onClose={() => {
            setShowEditModal(false);
            setEditModalSection('general');
          }}
        />
      )}

      {/* Universal Call Modal (manual staff initiation only) */}
      {canOperateClients && showCallModal && clientDetail && (
        <UniversalCallModal
          initialClient={clientDetail.client}
          onClose={() => setShowCallModal(false)}
        />
      )}

      {/* Send Fax Modal */}
      {canOperateClients && showFaxModal && clientDetail && (
        <ClientFaxModal
          clientId={clientDetail.client.id}
          clientName={`${clientDetail.client.first_name} ${clientDetail.client.last_name}`}
          clientDocuments={clientDetail.documents || []}
          onClose={() => setShowFaxModal(false)}
        />
      )}

      {chartRemoteSignatureAssignment && clientDetail && (
        <ManualSoapApprovalModal
          assignment={chartRemoteSignatureAssignment}
          clientLabel={`${clientDetail.client.last_name}, ${clientDetail.client.first_name}`}
          onClose={() => setChartRemoteSignatureAssignment(null)}
          onSaved={async () => {
            setChartRemoteSignatureAssignment(null);
            if (selectedClientId) await fetchClientDetail(selectedClientId);
          }}
        />
      )}

      {/* Assign Provider Modal */}
      {canOperateClients && showAssignModal && clientDetail && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 w-full max-w-md rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-slate-700" />
                <h3 className="text-sm font-bold text-slate-900">Assign Treating Provider</h3>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveAssignment} className="space-y-3 text-xs">
              <p className="text-slate-600">
                Assign client <strong className="text-slate-900">{clientDetail.client.first_name} {clientDetail.client.last_name}</strong> to a treating provider:
              </p>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">Treating Provider</label>
                <select
                  required
                  value={assignForm.provider_id}
                  onChange={(e) => {
                    const provId = e.target.value;
                    const prov = allProviders.find(p => p.id === provId);
                    setAssignForm({
                      ...assignForm,
                      provider_id: provId,
                      supervisor_id: prov?.supervisor_id || ''
                    });
                  }}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Choose provider...</option>
                  {allProviders.map(p => {
                    const rateLabel = (p.rate_per_session !== null && p.rate_per_session !== undefined && Number(p.rate_per_session) > 0)
                      ? ` - $${p.rate_per_session}/session`
                      : ' - Standard Pay Schedule';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.role}){rateLabel}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">Supervising SLP (Required for Assistants)</label>
                <select
                  value={assignForm.supervisor_id}
                  onChange={(e) => setAssignForm({ ...assignForm, supervisor_id: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">None / Direct SLP</option>
                  {allProviders.filter(p => p.role === 'SUPERVISOR' || p.role === 'SLP').map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Expected Frequency</label>
                  <input
                    type="text"
                    value={assignForm.expected_frequency}
                    onChange={(e) => setAssignForm({ ...assignForm, expected_frequency: e.target.value })}
                    placeholder="e.g. 2x/week"
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">Planned Duration (Min)</label>
                  <input
                    type="number"
                    value={assignForm.expected_duration_minutes}
                    onChange={(e) => setAssignForm({ ...assignForm, expected_duration_minutes: Number(e.target.value) })}
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">Notes (Optional)</label>
                <textarea
                  rows={2}
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                  placeholder="Caseload assignment notes..."
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold shadow-xs"
                >
                  Save Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Upload Document Modal */}
      {showUploadDocModal && clientDetail && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 max-w-lg w-full rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 bg-slate-50/80 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                  <Upload className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Upload Client Document</h3>
                  <p className="text-[11px] text-slate-500">Store clinical document or physician-signed POC on chart</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowUploadDocModal(false);
                  setUploadDocError(null);
                  setUploadDocSuccess(null);
                }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUploadDocumentSubmit} className="p-5 space-y-3.5 text-xs">
              {/* Client Context Banner */}
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-slate-700">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-500 block">Active Client</span>
                  <span className="font-bold text-slate-900 text-xs">{clientDetail.client.last_name}, {clientDetail.client.first_name}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] uppercase font-bold text-slate-500 block">ID / Payer</span>
                  <span className="font-mono text-[11px] text-slate-600">{clientDetail.client.id} • {clientDetail.client.primary_payer_name}</span>
                </div>
              </div>

              {/* File Upload Selector */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Select Document File *</label>
                <div className="border-2 border-dashed border-slate-200 hover:border-slate-400 rounded-xl p-3 bg-slate-50/50 text-center transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff"
                    onChange={handleDocFileSelect}
                    className="hidden"
                    id="client-doc-file-input"
                  />
                  <label
                    htmlFor="client-doc-file-input"
                    className="cursor-pointer flex flex-col items-center justify-center space-y-1.5"
                  >
                    <Upload className="w-6 h-6 text-slate-500" />
                    {uploadDocForm.file_name ? (
                      <div>
                        <span className="text-slate-900 font-medium block">{uploadDocForm.file_name}</span>
                        <span className="text-[10px] text-slate-500">
                          {Math.round(uploadDocForm.file_size / 1024)} KB • Click to change file
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className="text-slate-800 font-medium block">Click or browse to select document (PDF, Images)</span>
                        <span className="text-[10px] text-slate-500">Supports PDF, TIFF, DOCX, Images up to 25MB</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Document Classification */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Document Type *</label>
                <select
                  value={uploadDocForm.doc_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setUploadDocForm(prev => ({
                      ...prev,
                      doc_type: newType,
                      poc_end_date: (isSpeechOrFeedingPoc(newType)) ? (prev.poc_end_date || clientDetail.client.poc_end_date || '') : prev.poc_end_date
                    }));
                  }}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 font-semibold text-xs focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  {CLINICAL_UPLOAD_DOC_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Document Title */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Document Title</label>
                <input
                  type="text"
                  value={uploadDocForm.title}
                  onChange={(e) => setUploadDocForm({ ...uploadDocForm, title: e.target.value })}
                  placeholder="e.g. Physician-Signed CPOC (Dr. Smith)"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>

              {/* Contextual Date Fields: Show POC Expiration Date only for POC types */}
              {isSpeechOrFeedingPoc(uploadDocForm.doc_type) ? (
                <div className="grid grid-cols-2 gap-3">
                  {/* Document Date */}
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Document Date {uploadDocForm.doc_type === 'PHYSICIAN_SIGNED_POC' ? '*' : ''}
                    </label>
                    <input
                      type="date"
                      required={uploadDocForm.doc_type === 'PHYSICIAN_SIGNED_POC'}
                      value={uploadDocForm.document_date}
                      onChange={(e) => setUploadDocForm({ ...uploadDocForm, document_date: e.target.value })}
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400 font-mono"
                    />
                  </div>

                  {/* POC Expiration Date */}
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      POC Expiration Date {uploadDocForm.doc_type === 'PHYSICIAN_SIGNED_POC' ? '*' : '(Optional)'}
                    </label>
                    <input
                      type="date"
                      required={uploadDocForm.doc_type === 'PHYSICIAN_SIGNED_POC'}
                      value={uploadDocForm.poc_end_date}
                      onChange={(e) => setUploadDocForm({ ...uploadDocForm, poc_end_date: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400 font-mono"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  {/* Document Date for non-POC documents */}
                  <label className="block text-slate-700 font-semibold mb-1">Document Date</label>
                  <input
                    type="date"
                    value={uploadDocForm.document_date}
                    onChange={(e) => setUploadDocForm({ ...uploadDocForm, document_date: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400 font-mono"
                  />
                </div>
              )}

              {/* Notes / Annotation */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Notes / Description (Optional)</label>
                <textarea
                  rows={2}
                  value={uploadDocForm.notes}
                  onChange={(e) => setUploadDocForm({ ...uploadDocForm, notes: e.target.value })}
                  placeholder="e.g. Signed POC returned by pediatrician office via mail/email..."
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>

              {uploadDocError && (
                <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center space-x-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{uploadDocError}</span>
                </div>
              )}

              {uploadDocSuccess && (
                <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center space-x-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{uploadDocSuccess}</span>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadDocModal(false);
                    setUploadDocError(null);
                    setUploadDocSuccess(null);
                  }}
                  className="px-3.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingDoc}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-semibold text-xs transition-colors flex items-center space-x-1.5 shadow-xs"
                >
                  {uploadingDoc ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      <span>Upload to Chart</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Client Note Modal (Client Overview Inline Entry) */}
      {showAddClientNoteModal && clientDetail?.client && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 w-full max-w-md rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4 text-slate-700" />
                <h3 className="text-sm font-bold text-slate-900">Add Client Note</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddClientNoteModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveClientNote} className="space-y-3 text-xs">
              <p className="text-slate-600">
                Record an operational note for{' '}
                <strong className="text-slate-900">
                  {clientDetail.client.first_name} {clientDetail.client.last_name} ({clientDetail.client.id})
                </strong>:
              </p>

              {clientNoteError && (
                <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                  {clientNoteError}
                </div>
              )}

              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                  Category
                </label>
                <select
                  value={clientNoteCategory}
                  onChange={(e) => setClientNoteCategory(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="GENERAL">General Operational Note</option>
                  <option value="COORDINATION">Care Coordination</option>
                  <option value="SCHEDULING">Caseload / Scheduling</option>
                  <option value="CONTACT">Family / Guardian Contact</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                  Note Content
                </label>
                <textarea
                  rows={4}
                  required
                  value={clientNoteText}
                  onChange={(e) => {
                    setClientNoteText(e.target.value);
                    if (clientNoteError) setClientNoteError(null);
                  }}
                  placeholder="Enter note or coordination details..."
                  className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddClientNoteModal(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingClientNote}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center space-x-1.5 disabled:opacity-50 shadow-xs"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{savingClientNote ? 'Saving...' : 'Save Note'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {canOperateClients && showAddAddressModal && clientDetail?.client && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 w-full max-w-md rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-slate-700" />
                <h3 className="text-sm font-bold text-slate-900">Add Address</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddAddressModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleAddClientAddress} className="space-y-3 text-xs">
              {addressActionError && (
                <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                  {addressActionError}
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Label</label>
                <input
                  required
                  value={addressForm.label}
                  onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                  placeholder="Home"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Role</label>
                <select
                  value={addressForm.role}
                  onChange={(e) => setAddressForm({ ...addressForm, role: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="HOME">Home</option>
                  <option value="TREATMENT">Treatment</option>
                  <option value="SCHOOL">School</option>
                  <option value="ABA_CENTER">ABA Center</option>
                  <option value="GUARDIAN">Guardian</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Address</label>
                <textarea
                  rows={3}
                  required
                  value={addressForm.formatted}
                  onChange={(e) => setAddressForm({ ...addressForm, formatted: e.target.value })}
                  placeholder="Street, city, state ZIP"
                  className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddAddressModal(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingAddress}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-50 shadow-xs"
                >
                  {savingAddress ? 'Saving...' : 'Save Address'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddPhoneModal && clientDetail?.client && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 w-full max-w-md rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-slate-700" />
                <h3 className="text-sm font-bold text-slate-900">Add Phone</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddPhoneModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleAddClientPhone} className="space-y-3 text-xs">
              {phoneActionError && (
                <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                  {phoneActionError}
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Label</label>
                <select
                  value={phoneForm.label}
                  onChange={(e) => setPhoneForm({ ...phoneForm, label: e.target.value })}
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                >
                  <option value="Client">Client</option>
                  <option value="Mom">Mom</option>
                  <option value="Dad">Dad</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Home">Home</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Number</label>
                <input
                  required
                  value={phoneForm.number}
                  onChange={(e) => setPhoneForm({ ...phoneForm, number: e.target.value })}
                  placeholder="555-0100"
                  className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-slate-400"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddPhoneModal(false)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPhone}
                  className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-50 shadow-xs"
                >
                  {savingPhone ? 'Saving...' : 'Save Phone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark Inactive Confirmation Modal */}
      {canChangeClientStatus && showInactiveConfirmModal && clientDetail?.client && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl max-w-md w-full p-5 space-y-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
                  <UserX className="w-5 h-5 text-rose-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900 tracking-tight">
                    Mark Client Inactive
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Are you sure you want to mark <span className="font-semibold text-slate-900">{clientDetail.client.first_name} {clientDetail.client.last_name}</span> as Inactive?
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInactiveConfirmModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors shrink-0 cursor-pointer"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[11.5px] text-slate-600 space-y-1">
              <div className="text-slate-900 font-medium flex items-center space-x-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span>Full Clinical Data Preserved</span>
              </div>
              <p className="text-slate-500 pl-5 leading-relaxed">
                All authorizations, goals, clinical documents, SOAPs, billing history, and contacts remain completely preserved and accessible in the client chart.
              </p>
            </div>

            <div className="flex justify-end items-center space-x-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={statusUpdating}
                onClick={() => setShowInactiveConfirmModal(false)}
                className="px-3.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={statusUpdating}
                onClick={async () => {
                  await handleToggleClientStatus('INACTIVE');
                  setShowInactiveConfirmModal(false);
                }}
                className="px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold text-xs shadow-xs transition-all cursor-pointer disabled:opacity-50 inline-flex items-center space-x-1.5"
              >
                <UserX className="w-3.5 h-3.5" />
                <span>{statusUpdating ? 'Updating...' : 'Confirm Inactive'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
